import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { UserButton } from '@clerk/clerk-react';
import AdminSidebar from '../components/AdminSidebar';
import './Admin.css';

// Package ID → prijs en naam (voor weergave per seller)
const PACKAGE_PRICES = {
  1: { name: 'Pakket B - 2026', price: 49, type: 'Subscription', billing: 'Monthly' },
  2: { name: 'Pakket A - 2026', price: 29, type: 'Subscription', billing: 'Monthly' },
  3: { name: 'Platform connection (Small)', price: 19, type: 'Subscription', billing: 'Monthly' },
  4: { name: 'Pakket C - 2026', price: 99, type: 'Subscription', billing: 'Monthly' },
  5: { name: 'Productdeal.com CSS', price: 24.95, type: 'Add On', billing: 'Monthly' },
  6: { name: 'CPC (Cost per click)', price: 39.95, type: 'Add On', billing: 'Monthly' },
  7: { name: 'Pakket A - 2026 Yearly', price: 245, type: 'Subscription', billing: 'Yearly' },
  8: { name: 'Pakket B - 2026 Yearly', price: 415, type: 'Subscription', billing: 'Yearly' },
  9: { name: 'Pakket C - 2026 Yearly', price: 825, type: 'Subscription', billing: 'Yearly' },
};
const getPackageId = (p) => {
  const raw = p?.id ?? p?.package_id ?? p?.entity_id;
  return raw != null ? Number(raw) : null;
};
// Fallback maandbedrag als we alleen type hebben (geen id in 1-9)
const PACKAGE_TYPE_MONTHLY_FALLBACK = {
  subscription: 29,
  Subscription: 29,
  'Add On': 0,
  'add on': 0,
};
const getPackageShortLabel = (p) => {
  const id = getPackageId(p);
  const info = id != null ? PACKAGE_PRICES[id] : null;
  if (info) return `ID ${id}: €${Number(info.price) === info.price ? info.price : info.price.toFixed(2)}`;
  return p?.packages_type || p?.name || (id != null ? `ID ${id}` : '-');
};
// Maandelijkse revenue voor één package (yearly → price/12); fallback op type als id ontbreekt
const getPackageMonthlyRevenue = (p) => {
  const id = getPackageId(p);
  const info = id != null ? PACKAGE_PRICES[id] : null;
  if (info) {
    const price = Number(info.price);
    if (String(info.billing || '').toLowerCase() === 'yearly') return price / 12;
    return price;
  }
  const type = (p?.packages_type || p?.package_type || p?.name || '').trim();
  if (type && PACKAGE_TYPE_MONTHLY_FALLBACK[type] !== undefined) return PACKAGE_TYPE_MONTHLY_FALLBACK[type];
  if (/subscription/i.test(type)) return 29;
  return 0;
};
const getMonthlyRevenueForPackages = (packages) => {
  if (!packages || !packages.length) return 0;
  return packages.reduce((sum, p) => sum + getPackageMonthlyRevenue(p), 0);
};

/** Weergavevolgorde voor admin Saldo-totalen (balance API) */
const ADMIN_BALANCE_TOTAL_KEYS = [
  'balance_pending',
  'balance_total',
  'balance_available',
  'balance_refund',
  'balance_partner',
  'balance_commission',
  'balance_pending_partner_plus',
  'balance_pending_partner_minus',
  'balance_pending_partner_net',
  'balance_available_partner',
];

const ADMIN_BALANCE_TOTAL_LABELS = {
  balance_pending: 'Pending',
  balance_total: 'Totaal (bevestigd)',
  balance_available: 'Beschikbaar',
  balance_refund: 'Refund',
  balance_partner: 'Partner',
  balance_commission: 'Commissie',
  balance_pending_partner_plus: 'Pending partner (plus)',
  balance_pending_partner_minus: 'Pending partner (min)',
  balance_pending_partner_net: 'Pending partner (totaal)',
  balance_available_partner: 'Beschikbaar partner',
};

function sortAdminBalanceTotalKeys(keys) {
  return [...keys].sort((a, b) => {
    const ia = ADMIN_BALANCE_TOTAL_KEYS.indexOf(a);
    const ib = ADMIN_BALANCE_TOTAL_KEYS.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

const Admin = () => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [financeNegativeStats, setFinanceNegativeStats] = useState(null);
  const [financeBalanceStatsError, setFinanceBalanceStatsError] = useState(null);
  const [financeOutreachSettings, setFinanceOutreachSettings] = useState(null);
  const [financeOutreachLogs, setFinanceOutreachLogs] = useState([]);
  const [financeOutreachMonthlyStats, setFinanceOutreachMonthlyStats] = useState([]);
  const [financeOutreachLoadError, setFinanceOutreachLoadError] = useState(null);
  const [financeOutreachSaving, setFinanceOutreachSaving] = useState(false);
  const [financeOutreachSnapshotBusy, setFinanceOutreachSnapshotBusy] = useState(false);
  const [financeOutreachRunBusy, setFinanceOutreachRunBusy] = useState(false);
  const [financeOutreachRunMessage, setFinanceOutreachRunMessage] = useState(null);
  const [financeOutreachRunResult, setFinanceOutreachRunResult] = useState(null);
  const [financeSnapshotSummary, setFinanceSnapshotSummary] = useState(null);
  const [sellerWeeklyAnalytics, setSellerWeeklyAnalytics] = useState(null);
  const [sellerWeeklySnapshotBusy, setSellerWeeklySnapshotBusy] = useState(false);
  const [balanceTotalsData, setBalanceTotalsData] = useState(null);
  const [balanceTotalsLoading, setBalanceTotalsLoading] = useState(false);
  const [balanceTotalsError, setBalanceTotalsError] = useState(null);
  const [customersData, setCustomersData] = useState(null);
  const [sellersData, setSellersData] = useState(null);
  const [sellersStatusFilter, setSellersStatusFilter] = useState('all'); // 'all' | 'enabled' | 'disabled'
  const [sellersIncludePackages, setSellersIncludePackages] = useState(false);
  const [sellersPreferLive, setSellersPreferLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [loadingSellerInfo, setLoadingSellerInfo] = useState(false);
  const [sellerInfoError, setSellerInfoError] = useState(null);
  
  // Mailing section state
  const [mailingSubsection, setMailingSubsection] = useState('consumers');
  const [mailingData, setMailingData] = useState({
    consumers: null,
    templates: null,
    workflows: null,
    campaigns: null,
    bounces: null,
    lists: null
  });
  const [mailingLoading, setMailingLoading] = useState(false);
  
  // Workflow editor state
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowForm, setWorkflowForm] = useState({
    name: '',
    description: '',
    triggerType: 'consumer_created',
    triggerConditions: {},
    isActive: true,
    steps: []
  });
  const [workflowLoading, setWorkflowLoading] = useState(false);
  
  // Lists state
  const [editingList, setEditingList] = useState(null);
  const [listForm, setListForm] = useState({
    name: '',
    description: '',
    isPublic: false,
    doubleOptIn: true
  });
  const [listLoading, setListLoading] = useState(false);
  
  // List members management state
  const [viewingListMembers, setViewingListMembers] = useState(null);
  const [listMembers, setListMembers] = useState(null);
  const [listMembersLoading, setListMembersLoading] = useState(false);
  const [listMemberStatusFilter, setListMemberStatusFilter] = useState('');
  const [selectedConsumers, setSelectedConsumers] = useState(new Set());
  const [bulkAddToListId, setBulkAddToListId] = useState(null);
  
  // Consumer editor state
  const [editingConsumer, setEditingConsumer] = useState(null);
  const [consumerForm, setConsumerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    store: 'NL',
    country: '',
    phone: '',
    source: 'manual',
    sourceUrl: ''
  });
  const [consumerLoading, setConsumerLoading] = useState(false);
  const [consumerSearchQuery, setConsumerSearchQuery] = useState('');
  const [consumerSearchTimeout, setConsumerSearchTimeout] = useState(null);
  const [consumerStoreFilter, setConsumerStoreFilter] = useState('');
  const [consumerCountryFilter, setConsumerCountryFilter] = useState('');
  const [consumerUnsubscribedFilter, setConsumerUnsubscribedFilter] = useState('');
  
  // Campaign editor state
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    subject: '',
    templateId: '',
    listId: '',
    scheduledAt: '',
    status: 'draft'
  });
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignPreview, setCampaignPreview] = useState('');
  const [recipientCount, setRecipientCount] = useState(0);
  
  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncPolling, setSyncPolling] = useState(false);
  const [startEntityId, setStartEntityId] = useState('');
  const [maxPages, setMaxPages] = useState('10'); // Default: 10 pages = 5000 orders
  
  // Template editor state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    category: '',
    isActive: true,
    availableVariables: []
  });
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templatePreview, setTemplatePreview] = useState('');
  
  // Available variables for templates
  const availableVariables = [
    { name: 'firstName', label: 'Voornaam', description: 'Voornaam van de consument', example: 'Jan' },
    { name: 'lastName', label: 'Achternaam', description: 'Achternaam van de consument', example: 'Jansen' },
    { name: 'email', label: 'Email', description: 'Email adres van de consument', example: 'jan@example.com' },
    { name: 'store', label: 'Store', description: 'Store code (NL, DE, BE, FR)', example: 'NL' },
    { name: 'country', label: 'Land', description: 'Land code', example: 'NL' },
    { name: 'unsubscribeLink', label: 'Uitschrijflink', description: 'Link om uit te schrijven', example: 'https://...' },
    { name: 'unsubscribeToken', label: 'Uitschrijftoken', description: 'Unieke token voor uitschrijven', example: 'abc123...' }
  ];

  // Helper function to make authenticated requests (uses Clerk session token)
  const authenticatedFetch = async (url, options = {}) => {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      handleLogout();
      throw new Error('Authentication required');
    }
    return response;
  };

  const fetchBalanceTotals = async () => {
    setBalanceTotalsLoading(true);
    setBalanceTotalsError(null);
    try {
      const response = await authenticatedFetch('/api/admin/balance-totals');
      const result = await response.json();
      if (!result.success) {
        setBalanceTotalsError(result.error || 'Ophalen mislukt');
        setBalanceTotalsData(null);
        return;
      }
      setBalanceTotalsData(result.data);
    } catch (error) {
      setBalanceTotalsError(error.message || 'Fout bij ophalen');
      setBalanceTotalsData(null);
    } finally {
      setBalanceTotalsLoading(false);
    }
  };

  const patchFinanceOutreachSettings = async (partial) => {
    setFinanceOutreachSaving(true);
    try {
      const res = await authenticatedFetch('/api/admin/finance-outreach-settings', {
        method: 'PATCH',
        body: JSON.stringify(partial),
      });
      const json = await res.json();
      if (json.success) setFinanceOutreachSettings(json.data);
    } catch (e) {
      console.error(e);
    } finally {
      setFinanceOutreachSaving(false);
    }
  };

  const runFinanceNegativeSnapshot = async () => {
    setFinanceOutreachSnapshotBusy(true);
    setFinanceOutreachRunMessage(null);
    try {
      const res = await authenticatedFetch('/api/admin/finance-negative-snapshot', { method: 'POST', body: '{}' });
      const json = await res.json();
      if (json.success) {
        const y = json.data?.year;
        const m = String(json.data?.month ?? '').padStart(2, '0');
        const n = json.data?.negativeCount ?? 0;
        const cr = json.data?.created ?? 0;
        const up = json.data?.updated ?? 0;
        const rm = json.data?.removed ?? 0;
        setFinanceOutreachRunMessage(
          `Snapshot opgeslagen: ${n} negatieve suppliers voor ${y}-${m} (${cr} nieuw, ${up} bijgewerkt${
            rm ? `, ${rm} verwijderd (niet meer negatief)` : ''
          }).`
        );
        const snapRes = await authenticatedFetch('/api/admin/finance-negative-snapshot');
        const snapJson = await snapRes.json();
        if (snapJson.success && snapJson.data) setFinanceSnapshotSummary(snapJson.data);
      } else {
        setFinanceOutreachRunMessage(json.error || 'Snapshot mislukt');
      }
    } catch (e) {
      setFinanceOutreachRunMessage(e.message || 'Snapshot mislukt');
    } finally {
      setFinanceOutreachSnapshotBusy(false);
    }
  };

  const runFinanceOutreach = async (dryRun) => {
    setFinanceOutreachRunBusy(true);
    setFinanceOutreachRunMessage(null);
    setFinanceOutreachRunResult(null);
    try {
      const res = await authenticatedFetch('/api/admin/finance-outreach-run', {
        method: 'POST',
        body: JSON.stringify({ dryRun, limit: 8 }),
      });
      const json = await res.json();
      if (json.success) {
        setFinanceOutreachRunResult(json.data);
        setFinanceOutreachRunMessage(
          dryRun
            ? `Dry-run: ${json.data?.processedInThisRequest ?? 0} voorbeelden gelogd.`
            : `Verzonden: ${json.data?.processedInThisRequest ?? 0} sellers (batch).`
        );
        const logRes = await authenticatedFetch('/api/admin/finance-outreach-logs?limit=40');
        const logJson = await logRes.json();
        if (logJson.success && logJson.data?.logs) setFinanceOutreachLogs(logJson.data.logs);
        const moRes = await authenticatedFetch('/api/admin/finance-outreach-monthly-stats?limitMonths=36');
        const moJson = await moRes.json();
        if (moJson.success && Array.isArray(moJson.data?.months)) {
          setFinanceOutreachMonthlyStats(moJson.data.months);
        }
      } else {
        setFinanceOutreachRunMessage(json.error || 'Run mislukt');
      }
    } catch (e) {
      setFinanceOutreachRunMessage(e.message || 'Run mislukt');
    } finally {
      setFinanceOutreachRunBusy(false);
    }
  };

  // Set authenticated when Clerk is loaded and user is signed in (ProtectedAdminRoute ensures admin role)
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      setIsAuthenticated(true);
    }
  }, [isLoaded, isSignedIn]);

  const fetchSectionData = async (section, opts = {}) => {
    setLoading(true);
    try {
      if (section === 'finance') {
        setFinanceBalanceStatsError(null);
        setFinanceNegativeStats(null);
        setFinanceOutreachLoadError(null);
        const [finRes, balRes, outSetRes, outLogRes, outMoRes, snapRes] = await Promise.all([
          authenticatedFetch('/api/admin/finance'),
          authenticatedFetch('/api/admin/balance-totals'),
          authenticatedFetch('/api/admin/finance-outreach-settings'),
          authenticatedFetch('/api/admin/finance-outreach-logs?limit=40'),
          authenticatedFetch('/api/admin/finance-outreach-monthly-stats?limitMonths=36'),
          authenticatedFetch('/api/admin/finance-negative-snapshot'),
        ]);
        const finJson = await finRes.json();
        const balJson = await balRes.json();
        const outSetJson = await outSetRes.json();
        const outLogJson = await outLogRes.json();
        const outMoJson = await outMoRes.json();
        const snapJson = await snapRes.json();
        if (finJson.success) {
          setFinanceData(finJson.data);
        } else {
          console.error('Error fetching finance:', finJson.error);
          setFinanceData(null);
        }
        if (balJson.success && balJson.data?.negativePendingPartner) {
          setFinanceNegativeStats(balJson.data.negativePendingPartner);
        } else {
          setFinanceNegativeStats(null);
          setFinanceBalanceStatsError(
            balJson.error || 'Negatieve saldo-statistieken niet beschikbaar (balance-totals).'
          );
        }
        if (outSetJson.success) {
          setFinanceOutreachSettings(outSetJson.data);
        } else {
          setFinanceOutreachSettings(null);
          setFinanceOutreachLoadError(outSetJson.error || 'Outreach-instellingen niet geladen.');
        }
        if (outLogJson.success && outLogJson.data?.logs) {
          setFinanceOutreachLogs(outLogJson.data.logs);
        } else {
          setFinanceOutreachLogs([]);
        }
        if (outMoJson.success && Array.isArray(outMoJson.data?.months)) {
          setFinanceOutreachMonthlyStats(outMoJson.data.months);
        } else {
          setFinanceOutreachMonthlyStats([]);
        }
        if (snapJson.success && snapJson.data) {
          setFinanceSnapshotSummary(snapJson.data);
        } else {
          setFinanceSnapshotSummary(null);
        }
        return;
      }

      if (section === 'sellers') {
        setSellerWeeklyAnalytics(null);
        const sp = new URLSearchParams();
        if (opts.includePackages || sellersIncludePackages) sp.set('includePackages', '1');
        if (opts.live || sellersPreferLive) sp.set('live', '1');
        const q = sp.toString();
        let sellersUrl = '/api/admin/sellers-from-api';
        if (q) sellersUrl += `?${q}`;
        const [sellersResponse, weekRes] = await Promise.all([
          authenticatedFetch(sellersUrl),
          authenticatedFetch('/api/admin/seller-weekly-snapshots'),
        ]);
        const result = await sellersResponse.json();
        const weekJson = await weekRes.json();
        if (result.success) {
          setSellersData(result.data);
        } else {
          console.error('Error fetching data:', result.error);
        }
        if (weekJson.success && weekJson.data) {
          setSellerWeeklyAnalytics(weekJson.data);
        } else {
          setSellerWeeklyAnalytics(null);
        }
        return;
      }

      const url = `/api/admin/${section}`;
      const response = await authenticatedFetch(url);
      const result = await response.json();
      
      if (result.success) {
        if (section === 'overview') {
          setOverviewData(result.data);
        } else if (section === 'customers') {
          setCustomersData(result.data);
        }
      } else {
        console.error('Error fetching data:', result.error);
      }
    } catch (error) {
      console.error('Error fetching section data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMailingData = async (subsection, searchQuery = '', storeFilter = '', countryFilter = '', unsubscribedFilter = '') => {
    setMailingLoading(true);
    try {
      let endpoint = `/api/admin/mailing/${subsection}`;
      
      // Add search and filter parameters for consumers
      if (subsection === 'consumers') {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (storeFilter) params.append('store', storeFilter);
        if (countryFilter) params.append('country', countryFilter);
        if (unsubscribedFilter !== '') params.append('isUnsubscribed', unsubscribedFilter);
        
        const queryString = params.toString();
        if (queryString) {
          endpoint += `?${queryString}`;
        }
      }
      
      const response = await authenticatedFetch(endpoint);
      
      if (!response.ok) {
        const text = await response.text();
        console.error(`Error fetching mailing data (${response.status}):`, text);
        return;
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Error fetching mailing data: Response is not JSON:', text.substring(0, 200));
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        setMailingData(prev => ({
          ...prev,
          [subsection]: result.data
        }));
      } else {
        console.error('Error fetching mailing data:', result.error);
      }
    } catch (error) {
      console.error('Error fetching mailing data:', error);
    } finally {
      setMailingLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await authenticatedFetch('/api/admin/mailing/sync-customers');
      
      if (!response.ok) {
        const text = await response.text();
        console.error(`Error fetching sync status (${response.status}):`, text);
        setSyncPolling(false);
        return;
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Error fetching sync status: Response is not JSON:', text.substring(0, 200));
        setSyncPolling(false);
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        setSyncStatus(result.data);
        
        // Only poll if sync is actually running AND we're on the consumers page
        if (result.data.isRunning && mailingSubsection === 'consumers') {
          if (!syncPolling) {
            setSyncPolling(true);
          }
        } else {
          // Stop polling if sync is not running OR we're not on consumers page
          setSyncPolling(false);
          // Refresh consumers data when sync completes
          if (mailingSubsection === 'consumers' && !result.data.isRunning) {
            fetchMailingData('consumers', consumerSearchQuery, consumerStoreFilter, consumerCountryFilter, consumerUnsubscribedFilter);
          }
        }
      } else {
        // Stop polling on error
        setSyncPolling(false);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
      setSyncPolling(false);
    }
  };

  const startSync = async (testMode = false, fromBeginning = false) => {
    setSyncLoading(true);
    try {
      // Parse maxPages - if empty or invalid, use null (unlimited) for normal sync, 1 for test
      const maxPagesValue = testMode ? 1 : (maxPages && maxPages.trim() ? parseInt(maxPages) : null);
      
      const response = await authenticatedFetch('/api/admin/mailing/sync-customers', {
        method: 'POST',
        body: JSON.stringify({
          pageSize: 500,
          startPage: 1,
          maxPages: maxPagesValue, // Use user-specified max pages or null for unlimited
          fullSync: fromBeginning, // If fromBeginning is true, start from beginning
          resume: !fromBeginning, // If fromBeginning is true, don't resume from checkpoint
          startEntityId: startEntityId ? parseInt(startEntityId) : null // Start from specific entity ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Start polling for status
        setSyncPolling(true);
        fetchSyncStatus();
        if (testMode) {
          alert('Test import gestart! Maximaal 500 orders worden geïmporteerd.');
        } else {
          alert('Sync gestart! De sync loopt op de achtergrond en kan worden hervat als deze wordt gestopt.');
        }
      } else {
        alert('Error starting sync: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error starting sync:', error);
      alert('Error starting sync: ' + error.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const stopSync = async () => {
    if (!window.confirm('Weet je zeker dat je de sync wilt stoppen? De sync kan later worden hervat vanaf waar deze is gestopt.')) {
      return;
    }
    
    try {
      const response = await authenticatedFetch('/api/admin/mailing/sync-customers', {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert('Stop aangevraagd. De sync stopt na de huidige pagina.');
        fetchSyncStatus();
      } else {
        alert('Error stopping sync: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error stopping sync:', error);
      alert('Error stopping sync: ' + error.message);
    }
  };

  // Poll sync status when sync is running AND we're on consumers page
  useEffect(() => {
    // Only poll if:
    // 1. syncPolling is true (set by fetchSyncStatus when sync is running)
    // 2. We're on the consumers subsection
    // 3. We're authenticated
    if (syncPolling && mailingSubsection === 'consumers' && isAuthenticated) {
      const interval = setInterval(() => {
        fetchSyncStatus();
      }, 2000); // Poll every 2 seconds
      
      return () => clearInterval(interval);
    } else {
      // Stop polling if conditions are not met
      setSyncPolling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncPolling, mailingSubsection, isAuthenticated]);

  const searchCustomers = async (query) => {
    setIsSearching(true);
    try {
      const response = await authenticatedFetch(`/api/admin/search-customers?q=${encodeURIComponent(query)}`);
      const result = await response.json();
      
      if (result.success) {
        setSearchResults(result.data);
      } else {
        console.error('Error searching customers:', result.error);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch data when section changes
  useEffect(() => {
    if (isAuthenticated && activeSection) {
      if (activeSection === 'customers' && searchQuery.trim() !== '') {
        // Don't fetch default data if searching
        return;
      }
      if (activeSection === 'mailing') {
        // Always fetch mailing data for current subsection when section is active
        if (mailingSubsection === 'consumers') {
          fetchMailingData('consumers', consumerSearchQuery, consumerStoreFilter, consumerCountryFilter, consumerUnsubscribedFilter);
        } else {
          fetchMailingData(mailingSubsection);
        }
        // Also fetch sync status if on consumers subsection (one-time check, no polling unless sync is running)
        if (mailingSubsection === 'consumers') {
          fetchSyncStatus();
        } else {
          // Stop polling if we leave the consumers page
          setSyncPolling(false);
        }
        // Fetch lists if needed for bulk add
        if (bulkAddToListId && !mailingData.lists) {
          fetchMailingData('lists');
        }
      } else if (activeSection !== 'balance') {
        fetchSectionData(activeSection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeSection, mailingSubsection]);

  // Search customers
  useEffect(() => {
    if (isAuthenticated && activeSection === 'customers') {
      const timeoutId = setTimeout(() => {
        if (searchQuery.trim() !== '') {
          searchCustomers(searchQuery);
        } else {
          setSearchResults(null);
          // Reload default customers data
          if (!customersData) {
            fetchSectionData('customers');
          }
        }
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isAuthenticated, activeSection]);

  // Auto-update template preview when content changes
  useEffect(() => {
    if (editingTemplate) {
      const generatePreview = () => {
        let preview = templateForm.htmlContent || '';
        // Replace variables with example values
        availableVariables.forEach(variable => {
          const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
          preview = preview.replace(regex, variable.example);
        });
        setTemplatePreview(preview);
      };
      generatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateForm.htmlContent, templateForm.subject, editingTemplate]);

  // Auto-update campaign preview when content changes
  useEffect(() => {
    if (editingCampaign) {
      const updateCampaignPreview = () => {
        const selectedTemplate = mailingData.templates?.find(t => t.id === campaignForm.templateId);
        if (selectedTemplate) {
          let preview = selectedTemplate.htmlContent || '';
          // Replace variables with example values
          availableVariables.forEach(variable => {
            const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
            preview = preview.replace(regex, variable.example);
          });
          setCampaignPreview(preview);
        } else {
          setCampaignPreview('');
        }

        // Calculate recipient count
        if (campaignForm.listId) {
          const selectedList = mailingData.lists?.find(l => l.id === campaignForm.listId);
          if (selectedList) {
            setRecipientCount(selectedList.totalConsumers || selectedList._count?.listMembers || 0);
          } else {
            setRecipientCount(0);
          }
        } else {
          // Count all active consumers
          const consumers = mailingData.consumers?.consumers || [];
          setRecipientCount(consumers.length);
        }
      };
      updateCampaignPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignForm.templateId, campaignForm.listId, mailingData.templates, mailingData.lists]);

  const handleLogout = async () => {
    setIsAuthenticated(false);
    setSearchQuery('');
    setSearchResults(null);
    setSelectedSeller(null);
    setSellerInfo(null);
    await signOut();
    navigate('/');
  };

  const fetchSellerInfo = async (sellerId) => {
    if (!sellerId) return;
    
    setLoadingSellerInfo(true);
    setSellerInfoError(null);
    setSelectedSeller(sellerId);
    
    try {
      console.log(`[Admin] Fetching seller info for ID: ${sellerId}`);
      // Fetch all seller info in parallel (seller-admin + admin seller-view)
      const [balanceRes, ordersRes, deliveryRes, reviewsRes, viewRes] = await Promise.allSettled([
        fetch(`/api/seller-admin/balance?supplierId=${sellerId}`),
        fetch(`/api/seller-admin/orders?supplierId=${sellerId}&page=1&pageSize=10`),
        fetch(`/api/seller-admin/delivery-info?supplierId=${sellerId}`),
        fetch(`/api/seller-admin/reviews?supplierId=${sellerId}`),
        authenticatedFetch(`/api/admin/seller-view?supplierId=${encodeURIComponent(sellerId)}`)
      ]);

      const sellerData = {
        sellerId: sellerId,
        balance: null,
        orders: null,
        deliveryInfo: null,
        reviews: null,
        sellerView: null,
        errors: {}
      };

      // Process balance
      if (balanceRes.status === 'fulfilled' && balanceRes.value.ok) {
        const balanceData = await balanceRes.value.json();
        if (balanceData.success) {
          sellerData.balance = balanceData.data;
        } else {
          sellerData.errors.balance = balanceData.error || 'Failed to fetch balance';
        }
      } else {
        sellerData.errors.balance = 'Failed to fetch balance';
      }

      // Process orders
      if (ordersRes.status === 'fulfilled' && ordersRes.value.ok) {
        const ordersData = await ordersRes.value.json();
        if (ordersData.success) {
          // Sort orders by created_at date (newest first)
          if (ordersData.data && ordersData.data.items && Array.isArray(ordersData.data.items)) {
            ordersData.data.items.sort((a, b) => {
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateB - dateA; // Newest first (descending)
            });
          }
          sellerData.orders = ordersData.data;
        } else {
          sellerData.errors.orders = ordersData.error || 'Failed to fetch orders';
        }
      } else {
        sellerData.errors.orders = 'Failed to fetch orders';
      }

      // Process delivery info
      if (deliveryRes.status === 'fulfilled' && deliveryRes.value.ok) {
        const deliveryData = await deliveryRes.value.json();
        if (deliveryData.success) {
          sellerData.deliveryInfo = deliveryData.data;
        } else {
          sellerData.errors.deliveryInfo = deliveryData.error || 'Failed to fetch delivery info';
        }
      } else {
        sellerData.errors.deliveryInfo = 'Failed to fetch delivery info';
      }

      // Process reviews
      if (reviewsRes.status === 'fulfilled' && reviewsRes.value.ok) {
        const reviewsData = await reviewsRes.value.json();
        if (reviewsData.success) {
          sellerData.reviews = reviewsData.data;
        } else {
          sellerData.errors.reviews = reviewsData.error || 'Failed to fetch reviews';
        }
      } else {
        sellerData.errors.reviews = 'Failed to fetch reviews';
      }

      // Process supplier view (supplier + addresses + contacts + packages)
      if (viewRes.status === 'fulfilled' && viewRes.value && viewRes.value.ok) {
        const viewData = await viewRes.value.json();
        if (viewData.success && viewData.data) {
          sellerData.sellerView = viewData.data;
        } else {
          sellerData.errors.sellerView = viewData.error || 'Failed to fetch supplier view';
        }
      } else {
        sellerData.errors.sellerView = 'Failed to fetch supplier view';
      }

      setSellerInfo(sellerData);
    } catch (error) {
      console.error('Error fetching seller info:', error);
      setSellerInfoError('Failed to fetch seller information');
    } finally {
      setLoadingSellerInfo(false);
    }
  };

  const closeSellerModal = () => {
    setSelectedSeller(null);
    setSellerInfo(null);
    setSellerInfoError(null);
  };

  if (!isLoaded || !isAuthenticated) {
    return (
      <div className="admin-container">
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <div className="admin-loading">Loading...</div>
        </div>
      </div>
    );
  }

  const renderSectionContent = () => {
    if (loading) {
      return <div className="admin-loading">Loading...</div>;
    }

    switch (activeSection) {
      case 'overview':
        return (
          <div className="admin-section-content">
            <h2>Overzicht</h2>
            {overviewData && (
              <div className="admin-stats-grid">
                <div className="stat-card">
                  <h3>Aantal sellers</h3>
                  <p className="stat-number">{overviewData.totalEnabledSellers ?? '-'}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Enabled (database: MagentoSupplier, is_active=1 — zelfde sync als seller-lijst)
                  </p>
                </div>
                <div className="stat-card">
                  <h3>Sellers met balance</h3>
                  <p className="stat-number">{overviewData.enabledSellersWithNonZeroBalance ?? 0}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Alleen enabled uit database + minstens één saldo ≠ 0 (balance API)
                  </p>
                </div>
                <div className="stat-card">
                  <h3>New model sellers</h3>
                  <p className="stat-number">{overviewData.newModelCustomers ?? 0}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Alle package selections (database)</p>
                </div>
                <div className="stat-card">
                  <h3>Pakket A</h3>
                  <p className="stat-number">{overviewData.statsByPackage?.A ?? 0}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>New model</p>
                </div>
                <div className="stat-card">
                  <h3>Pakket B</h3>
                  <p className="stat-number">{overviewData.statsByPackage?.B ?? 0}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>New model</p>
                </div>
                <div className="stat-card">
                  <h3>Pakket C</h3>
                  <p className="stat-number">{overviewData.statsByPackage?.C ?? 0}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>New model</p>
                </div>
                <div className="stat-card">
                  <h3>Totaal Balance Sellers</h3>
                  <p className="stat-number">{overviewData.totalBalanceSellers ?? 0}</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Unieke supplier_id op saldo-endpoint (live API — niet de database-lijst)
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 'finance':
        return (
          <div className="admin-section-content">
            <h2>Finance</h2>
            <p style={{ color: '#666', marginBottom: '16px', maxWidth: '720px' }}>
              Negatieve saldo&apos;s op basis van <strong>balance_pending_partner</strong> per seller (alle saldoregels per supplier worden opgeteld). Zelfde bron als &quot;Totaal saldo&quot;.
            </p>
            {financeBalanceStatsError && (
              <div style={{ color: '#b45309', marginBottom: '16px', padding: '12px', background: '#fffbeb', borderRadius: '8px' }}>
                {financeBalanceStatsError}
              </div>
            )}
            {financeNegativeStats && (
              <div className="admin-stats-grid admin-finance-negative-grid" style={{ marginBottom: '28px' }}>
                <div className="stat-card admin-finance-negative-card">
                  <h3>Negatief saldo (totaal)</h3>
                  <p className="stat-number admin-finance-negative-amount">
                    €{Number(financeNegativeStats.negativeTotalEuro ?? 0).toFixed(2)}
                  </p>
                  <p className="admin-finance-negative-sub">Som van alle sellers met pending partner &lt; 0</p>
                </div>
                <div className="stat-card admin-finance-negative-card">
                  <h3>Aantal negatieve sellers</h3>
                  <p className="stat-number admin-finance-negative-count">
                    {financeNegativeStats.negativeSellerCount ?? 0}
                  </p>
                  <p className="admin-finance-negative-sub">Unieke suppliers met saldo onder nul</p>
                </div>
                <div className="stat-card admin-finance-negative-card admin-finance-negative-card--alert">
                  <h3>Meer dan €60 negatief</h3>
                  <p className="stat-number admin-finance-negative-count">
                    {financeNegativeStats.belowMinus60Count ?? 0}
                  </p>
                  <p className="admin-finance-negative-amount admin-finance-negative-amount--secondary">
                    €{Number(financeNegativeStats.belowMinus60TotalEuro ?? 0).toFixed(2)}
                  </p>
                  <p className="admin-finance-negative-sub">Aantal sellers en som van hun saldo&apos;s (&lt; −€60)</p>
                </div>
              </div>
            )}
            <div
              style={{
                marginTop: '8px',
                marginBottom: '28px',
                padding: '20px',
                background: '#f1f5f9',
                borderRadius: '10px',
                maxWidth: '960px',
                border: '1px solid #e2e8f0',
              }}
            >
              <h3 style={{ marginTop: 0, fontSize: '1.1rem' }}>Maandelijkse outreach (Pingen, e-mail, Mollie)</h3>
              <p style={{ color: '#475569', fontSize: '14px', lineHeight: 1.5, marginBottom: '16px' }}>
                Op de <strong>2e</strong> om 10:00 UTC: chargeback-balanscorrectie (vorige maand). Om{' '}
                <strong>11:00 UTC</strong> op de 2e: mislukte Weekly Payout seller-incasso’s (vorige maand)
                afboeken via <code>/api/admin/finance-failed-weekly-payout-automation</code>. Op de{' '}
                <strong>3e</strong> om 10:00 UTC legt <code>/api/admin/finance-negative-snapshot</code> de
                maandsnapshot vast (live saldi sync). Op de <strong>5e</strong> om 10:00 UTC draait{' '}
                <code>/api/admin/finance-monthly-automation</code> (mail + Pingen). Om{' '}
                <strong>11:00 UTC</strong> volgt het finance review KPI-mail: activiteit (nieuw/churn) over de{' '}
                <strong>vorige kalendermaand</strong>, negatieve snapshot + outreach van de{' '}
                <strong>3e van deze maand</strong> (begin-maand snapshot “over” de vorige). Plus cron health report.
                Elke maand met negatief saldo:{' '}
                <strong>e-mailherinnering</strong>{' '}
                met betaallink. Bij <strong>twee opeenvolgende maanden</strong> negatief: papieren brief via Pingen (aanmaning).
                Bij <strong>vier opeenvolgende maanden</strong> negatief: brief met <strong>wettelijke incassokosten (WIK)</strong>{' '}
                bovenop het openstaande saldo. Handmatig: snapshot-knop, daarna dry-run /
                live batch. Zet brieven en e-mail uit tot adressen en env klaar staan.
              </p>
              {financeOutreachLoadError && (
                <p style={{ color: '#b45309', marginBottom: '12px' }}>{financeOutreachLoadError}</p>
              )}
              {financeSnapshotSummary?.latestMonth != null &&
              financeSnapshotSummary.latestSnapshotSellerCount != null ? (
                <div
                  style={{
                    marginBottom: '14px',
                    padding: '12px 14px',
                    background: '#ecfeff',
                    border: '1px solid #a5f3fc',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#0e7490',
                  }}
                >
                  <strong>Laatste maandsnapshot in de database:</strong>{' '}
                  {financeSnapshotSummary.latestMonth.year}-
                  {String(financeSnapshotSummary.latestMonth.month).padStart(2, '0')} —{' '}
                  <strong>{financeSnapshotSummary.latestSnapshotSellerCount}</strong> suppliers met negatief saldo
                  opgeslagen
                  {financeSnapshotSummary.monthsRecorded != null ? (
                    <>
                      {' '}
                      ({financeSnapshotSummary.monthsRecorded} maand
                      {financeSnapshotSummary.monthsRecorded === 1 ? '' : 'en'} met historiek).
                    </>
                  ) : (
                    '.'
                  )}
                </div>
              ) : (
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                  Nog geen maandsnapshot in de database — gebruik de knop hieronder of de maandelijkse automation-cron.
                </p>
              )}
              {financeOutreachSettings && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={financeOutreachSettings.lettersEnabled}
                      disabled={financeOutreachSaving}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setFinanceOutreachSettings((s) => ({ ...s, lettersEnabled: v }));
                        patchFinanceOutreachSettings({ lettersEnabled: v });
                      }}
                    />
                    Papieren brieven (Pingen)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={financeOutreachSettings.emailsEnabled}
                      disabled={financeOutreachSaving}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setFinanceOutreachSettings((s) => ({ ...s, emailsEnabled: v }));
                        patchFinanceOutreachSettings({ emailsEnabled: v });
                      }}
                    />
                    E-mail (SendGrid)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={financeOutreachSettings.includeDisabledSellers}
                      disabled={financeOutreachSaving}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setFinanceOutreachSettings((s) => ({ ...s, includeDisabledSellers: v }));
                        patchFinanceOutreachSettings({ includeDisabledSellers: v });
                      }}
                    />
                    Ook disabled suppliers
                  </label>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>
                    Link-TTL: {financeOutreachSettings.paymentLinkTtlDays} dagen (pas aan via API PATCH indien nodig)
                  </span>
                </div>
              )}
              {financeOutreachSettings && (
                <div
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '14px',
                    color: '#334155',
                  }}
                >
                  <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '140px' }}>From (finance-mails):</span>
                    <input
                      type="email"
                      disabled={financeOutreachSaving}
                      placeholder="finance@sdeal.com"
                      defaultValue={financeOutreachSettings.sendgridFromFinance || ''}
                      key={financeOutreachSettings.updatedAt || financeOutreachSettings.id}
                      style={{
                        minWidth: '260px',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const prev = financeOutreachSettings.sendgridFromFinance || '';
                        if (v === prev) return;
                        patchFinanceOutreachSettings({
                          sendgridFromFinance: v ? v : null,
                        });
                      }}
                    />
                  </label>
                  <span style={{ fontSize: '12px', color: '#64748b', maxWidth: '520px' }}>
                    Alleen negatief-saldo outreach. Leeg = omgevingsvariabele <code>FINANCE_FROM_EMAIL</code>, anders{' '}
                    <code>FROM_EMAIL</code> (bijv. mailing@sdeal.com voor overige app-mail).
                  </span>
                </div>
              )}
              {financeOutreachSettings && (
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '14px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '13px',
                    color: '#334155',
                    maxWidth: '720px',
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '8px' }}>Brief (Pingen) na 2 negatieve maanden</strong>
                  <p style={{ margin: '0 0 10px', lineHeight: 1.5, color: '#475569' }}>
                    Zelfde inhoud als de formele e-mail (NL/EN), incl. betaallink en optioneel overschrijfblok. Vink hierboven{' '}
                    <strong>Papieren brieven (Pingen)</strong> aan als alles klaar staat.
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b' }}>
                    <strong>Vercel / server env (Pingen + afzender op brief-PDF):</strong>{' '}
                    <code>PINGEN_CLIENT_ID</code>, <code>PINGEN_CLIENT_SECRET</code>, <code>PINGEN_ORG_UUID</code>,{' '}
                    <code>PINGEN_STAGING=true</code> voor test (geen echte post). Ontvangeradres staat in de PDF (DIN-676 venster);{' '}
                    afzender/logo: <code>POSTGRID_FROM_ADDRESS_LINE1</code>, <code>POSTGRID_FROM_CITY</code>,{' '}
                    <code>POSTGRID_FROM_POSTAL_OR_ZIP</code>, optioneel <code>POSTGRID_FROM_COMPANY</code>,{' '}
                    <code>POSTGRID_LETTER_LOGO_URL</code>.{' '}
                    Zet <code>BASE_URL=https://www.sdeal.com</code> (Vercel) voor juiste betaallink en QR op de brief.
                    Mollie-webhooks voor saldo-topups gaan standaard naar <code>https://www.sdeal.com/api/package/payment-webhook</code>
                    (override: <code>BALANCE_TOPUP_PUBLIC_BASE_URL</code>). Magento-credit na betaling staat standaard aan;
                    zet <code>MAGENTO_CREDIT_BALANCE_ON_FINANCE_TOPUP=false</code> alleen om te disablen. Missers inhalen:{' '}
                    <code>node scripts/reconcile-balance-topup-credits.js --apply</code>.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                    <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      <span style={{ minWidth: '160px' }}>IBAN (op brief):</span>
                      <input
                        type="text"
                        disabled={financeOutreachSaving}
                        placeholder="NL…"
                        defaultValue={financeOutreachSettings.manualPaymentIban || ''}
                        key={`iban-${financeOutreachSettings.updatedAt || financeOutreachSettings.id}`}
                        style={{
                          minWidth: '280px',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                        }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          const prev = financeOutreachSettings.manualPaymentIban || '';
                          if (v === prev) return;
                          patchFinanceOutreachSettings({ manualPaymentIban: v || null });
                        }}
                      />
                    </label>
                    <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      <span style={{ minWidth: '160px' }}>Ten name van (op brief):</span>
                      <input
                        type="text"
                        disabled={financeOutreachSaving}
                        placeholder="Juridische naam begunstigde"
                        defaultValue={financeOutreachSettings.manualPaymentName || ''}
                        key={`payname-${financeOutreachSettings.updatedAt || financeOutreachSettings.id}`}
                        style={{
                          minWidth: '280px',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                        }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          const prev = financeOutreachSettings.manualPaymentName || '';
                          if (v === prev) return;
                          patchFinanceOutreachSettings({ manualPaymentName: v || null });
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                <button
                  type="button"
                  className="admin-btn secondary"
                  disabled={financeOutreachSnapshotBusy}
                  onClick={runFinanceNegativeSnapshot}
                >
                  {financeOutreachSnapshotBusy ? 'Snapshot…' : 'Maandsnapshot nu vastleggen'}
                </button>
                <button
                  type="button"
                  className="admin-btn secondary"
                  disabled={financeOutreachRunBusy}
                  onClick={() => runFinanceOutreach(true)}
                >
                  {financeOutreachRunBusy ? 'Bezig…' : 'Dry-run (8 sellers)'}
                </button>
                <button
                  type="button"
                  className="admin-btn primary"
                  disabled={financeOutreachRunBusy}
                  onClick={() => {
                    if (
                      !window.confirm(
                        'Live run: max 8 sellers in deze batch — e-mail elke maand; brief alleen bij 2 opeenvolgende negatieve maanden. Doorgaan?'
                      )
                    ) {
                      return;
                    }
                    runFinanceOutreach(false);
                  }}
                >
                  Live run (8 sellers)
                </button>
              </div>
              {financeOutreachRunMessage && (
                <p style={{ fontSize: '14px', color: '#334155', marginBottom: '8px' }}>{financeOutreachRunMessage}</p>
              )}
              {financeOutreachRunResult?.results?.length > 0 && (
                <div style={{ fontSize: '13px', color: '#475569', marginBottom: '12px' }}>
                  <strong>Laatste batch:</strong> periode{' '}
                  {financeOutreachRunResult.periodKey || financeOutreachRunResult.quarterKey},{' '}
                  {financeOutreachRunResult.dryRun ? 'dry-run' : 'live'}. Sellers:{' '}
                  {financeOutreachRunResult.results
                    .filter((r) => !r.skipped)
                    .map((r) => {
                      const bits = [r.outreachKind];
                      if (r.fourConsecutiveNegativeMonths) bits.push('4 maanden negatief (incasso)');
                      else if (r.twoConsecutiveNegativeMonths) bits.push('2 maanden negatief');
                      if (r.dryRun && r.wouldSendLetter) bits.push('dry: brief zou mee');
                      if (r.letterStatus && r.letterStatus !== 'skipped') {
                        bits.push(`brief:${r.letterStatus}`);
                      }
                      if (r.emailStatus && r.emailStatus !== 'skipped') bits.push(`mail:${r.emailStatus}`);
                      return `${r.sellerId} (${bits.join(', ')})`;
                    })
                    .join(', ') || '—'}
                </div>
              )}
              {(financeOutreachSettings || financeOutreachMonthlyStats.length > 0) && (
                <div style={{ marginTop: '8px', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '6px' }}>Per maand verstuurd</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 8px' }}>
                    Alleen <strong>live</strong> regels: e-mail = status verzonden; brief = Pingen verzonden. Dry-runs
                    tellen niet mee. <strong>Negatief saldo</strong> en <strong>sellers</strong> komen uit de
                    maandsnapshot (3e van de maand); — = geen snapshot voor die maand. <strong>Δ</strong> = verschil
                    t.o.v. vorige maand. <strong>Incasso</strong> = ≥4 opeenvolgende negatieve maanden (alleen
                    nieuwste maand).
                  </p>
                  {financeOutreachMonthlyStats.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Nog geen data voor deze tabel.</p>
                  ) : (
                    <div className="admin-table-container" style={{ maxHeight: '280px', overflow: 'auto' }}>
                      <table className="admin-table" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>Maand (periode)</th>
                            <th>E-mails</th>
                            <th>Brieven</th>
                            <th>Neg. sellers (snapshot)</th>
                            <th>Δ sellers</th>
                            <th>Incasso-traject</th>
                            <th>Totaal neg. saldo (snapshot)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financeOutreachMonthlyStats.map((m) => (
                            <tr key={m.quarterKey}>
                              <td>{m.quarterKey}</td>
                              <td>{m.emailsSent ?? 0}</td>
                              <td>{m.lettersSent ?? 0}</td>
                              <td>
                                {m.snapshotNegativeSellerCount != null ? m.snapshotNegativeSellerCount : '—'}
                              </td>
                              <td>
                                {m.negativeSellerDelta == null
                                  ? '—'
                                  : m.negativeSellerDelta > 0
                                    ? `+${m.negativeSellerDelta}`
                                    : m.negativeSellerDelta}
                              </td>
                              <td>
                                {m.incassoTrajectoryCount != null ? m.incassoTrajectoryCount : '—'}
                              </td>
                              <td>
                                {m.snapshotNegativeTotalEuro != null
                                  ? new Intl.NumberFormat('nl-NL', {
                                      style: 'currency',
                                      currency: 'EUR',
                                    }).format(m.snapshotNegativeTotalEuro)
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {financeOutreachLogs.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Recente log</h4>
                  <div className="admin-table-container" style={{ maxHeight: '280px', overflow: 'auto' }}>
                    <table className="admin-table" style={{ fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>Tijd</th>
                          <th>Periode</th>
                          <th>Seller</th>
                          <th>Soort</th>
                          <th>Brief</th>
                          <th>E-mail</th>
                          <th>Dry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financeOutreachLogs.map((log) => (
                          <tr key={log.id}>
                            <td>{new Date(log.createdAt).toLocaleString('nl-NL')}</td>
                            <td>{log.quarterKey}</td>
                            <td>{log.sellerId}</td>
                            <td>{log.outreachKind}</td>
                            <td>{log.letterStatus}{log.postgridLetterId ? ` (${log.postgridLetterId})` : ''}</td>
                            <td>{log.emailStatus}{log.emailTo ? ` → ${log.emailTo}` : ''}</td>
                            <td>{log.dryRun ? 'ja' : 'nee'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {financeData && (
              <div>
                <h3 style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Recurring (Mollie)</h3>
                <div className="admin-stats-grid">
                  <div className="stat-card highlight">
                    <h3>Klanten met Recurring Payment</h3>
                    <p className="stat-number">{financeData.totalWithRecurring}</p>
                  </div>
                  <div className="stat-card highlight">
                    <h3>Geschatte Maandelijkse Omzet</h3>
                    <p className="stat-number">€{financeData.totalMonthlyRevenue}</p>
                  </div>
                </div>
                
                <div className="admin-stats-grid" style={{ marginTop: '20px' }}>
                  <div className="stat-card">
                    <h3>Pakket A (Recurring)</h3>
                    <p className="stat-number">{financeData.statsByPackage?.A || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Pakket B (Recurring)</h3>
                    <p className="stat-number">{financeData.statsByPackage?.B || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Pakket C (Recurring)</h3>
                    <p className="stat-number">{financeData.statsByPackage?.C || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Maandelijks</h3>
                    <p className="stat-number">{financeData.statsByBilling?.monthly || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Jaarlijks</h3>
                    <p className="stat-number">{financeData.statsByBilling?.yearly || 0}</p>
                  </div>
                </div>

                {financeData.customers && financeData.customers.length > 0 && (
                  <div style={{ marginTop: '30px' }}>
                    <h3>Klanten met Recurring Payments</h3>
                    <div className="admin-table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Seller ID</th>
                            <th>Email</th>
                            <th>Bedrijf</th>
                            <th>Pakket</th>
                            <th>Billing</th>
                            <th>Mollie Customer ID</th>
                            <th>Mandate ID</th>
                            <th>Datum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financeData.customers.map((customer) => (
                            <tr key={customer.id}>
                              <td>{customer.sellerId}</td>
                              <td>{customer.sellerEmail || '-'}</td>
                              <td>{customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '-'}</td>
                              <td>{customer.package}</td>
                              <td>{customer.billingPeriod || '-'}</td>
                              <td>{customer.mollieCustomerId || '-'}</td>
                              <td>{customer.mollieMandateId || '-'}</td>
                              <td>{new Date(customer.createdAt).toLocaleDateString('nl-NL')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'balance':
        return (
          <div className="admin-section-content">
            <h2>Totaal saldo alle sellers</h2>
            <p style={{ color: '#666', marginBottom: '16px', maxWidth: '640px' }}>
              Zelfde bron als het seller-dashboard (balance API): optelling van alle saldoregels. Klik op de knop om live te laden (kan even duren bij veel sellers).
            </p>
            <div style={{ marginBottom: '24px' }}>
              <button
                type="button"
                className="admin-btn primary"
                onClick={fetchBalanceTotals}
                disabled={balanceTotalsLoading}
              >
                {balanceTotalsLoading ? 'Bezig met laden…' : 'Totaal saldo ophalen'}
              </button>
            </div>
            {balanceTotalsError && (
              <div style={{ color: '#c00', marginBottom: '16px' }}>{balanceTotalsError}</div>
            )}
            {balanceTotalsData && (
              <div>
                {balanceTotalsData.truncated && (
                  <p style={{ color: '#b45309', marginBottom: '16px' }}>
                    Waarschuwing: niet alle pagina’s zijn opgehaald (limiet bereikt). Totalen kunnen onvolledig zijn.
                  </p>
                )}
                <h3 style={{ marginTop: '8px', marginBottom: '0', fontSize: '16px', color: '#444' }}>
                  Som alle sellers (balance_*)
                </h3>
                {balanceTotalsData.totals && Object.keys(balanceTotalsData.totals).length > 0 ? (
                  <div className="admin-balance-totals-grid">
                    {sortAdminBalanceTotalKeys(Object.keys(balanceTotalsData.totals)).map((key) => {
                      const raw = Number(balanceTotalsData.totals[key] ?? 0);
                      const neg = raw < 0;
                      return (
                        <div key={key} className="admin-balance-mini-card">
                          <h4>{ADMIN_BALANCE_TOTAL_LABELS[key] || key.replace(/^balance_/, '').replace(/_/g, ' ')}</h4>
                          <p className={`admin-balance-mini-amount${neg ? ' admin-balance-mini-amount--negative' : ''}`}>
                            €{raw.toFixed(2)}
                          </p>
                          <span className="admin-balance-mini-key">{key}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#666', marginTop: '12px' }}>Geen balance-velden in de response (0 saldoregels).</p>
                )}
                {(balanceTotalsData.pendingPartnerEnabledPlus !== undefined ||
                  balanceTotalsData.pendingPartnerEnabledMinus !== undefined) && (
                  <>
                    <h3 style={{ marginTop: '24px', marginBottom: '0', fontSize: '16px', color: '#444' }}>
                      balance_pending_partner (enabled vs disabled, plus / min)
                    </h3>
                    <p style={{ fontSize: '12px', color: '#777', marginTop: '6px', marginBottom: '0', maxWidth: '720px' }}>
                      <strong>Plus</strong> = som van positieve bedragen per saldoregel; <strong>min</strong> = som van
                      negatieve delen (≤ 0). <strong>Enabled</strong> = is_active=1 in supplier/list;{' '}
                      <strong>disabled</strong> = overige suppliers met saldoregel.
                    </p>
                    <h4 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
                      Enabled suppliers
                    </h4>
                    <div className="admin-balance-totals-grid" style={{ marginTop: '0' }}>
                      <div className="admin-balance-mini-card">
                        <h4>Enabled — plus</h4>
                        <p className="admin-balance-mini-amount">
                          €{Number(balanceTotalsData.pendingPartnerEnabledPlus ?? 0).toFixed(2)}
                        </p>
                        <span className="admin-balance-mini-key">Σ max(0, pending_partner) · enabled</span>
                      </div>
                      <div className="admin-balance-mini-card">
                        <h4>Enabled — min</h4>
                        <p
                          className={`admin-balance-mini-amount${
                            Number(balanceTotalsData.pendingPartnerEnabledMinus) < 0
                              ? ' admin-balance-mini-amount--negative'
                              : ''
                          }`}
                        >
                          €{Number(balanceTotalsData.pendingPartnerEnabledMinus ?? 0).toFixed(2)}
                        </p>
                        <span className="admin-balance-mini-key">Σ min(0, pending_partner) · enabled</span>
                      </div>
                    </div>
                    <h4 style={{ marginTop: '18px', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
                      Disabled / overig
                    </h4>
                    <div className="admin-balance-totals-grid" style={{ marginTop: '0' }}>
                      <div className="admin-balance-mini-card">
                        <h4>Disabled — plus</h4>
                        <p className="admin-balance-mini-amount">
                          €{Number(balanceTotalsData.pendingPartnerDisabledPlus ?? 0).toFixed(2)}
                        </p>
                        <span className="admin-balance-mini-key">Σ max(0, pending_partner) · niet enabled</span>
                      </div>
                      <div className="admin-balance-mini-card">
                        <h4>Disabled — min</h4>
                        <p
                          className={`admin-balance-mini-amount${
                            Number(balanceTotalsData.pendingPartnerDisabledMinus) < 0
                              ? ' admin-balance-mini-amount--negative'
                              : ''
                          }`}
                        >
                          €{Number(balanceTotalsData.pendingPartnerDisabledMinus ?? 0).toFixed(2)}
                        </p>
                        <span className="admin-balance-mini-key">Σ min(0, pending_partner) · niet enabled</span>
                      </div>
                    </div>
                  </>
                )}
                <div className="admin-balance-seller-row">
                  <div className="admin-balance-mini-card admin-balance-mini-card--sellers">
                    <h4>Sellers</h4>
                    <p className="admin-balance-mini-amount admin-balance-mini-amount--count">
                      {balanceTotalsData.sellerCount ?? '-'}
                    </p>
                    <span className="admin-balance-mini-key">uniek supplier_id</span>
                    <p className="admin-balance-seller-meta">
                      {balanceTotalsData.rowCount != null ? `${balanceTotalsData.rowCount} saldoregels` : ''}
                      {balanceTotalsData.reportedTotalCount != null
                        ? `${balanceTotalsData.rowCount != null ? ' · ' : ''}API totaal: ${balanceTotalsData.reportedTotalCount}`
                        : ''}
                    </p>
                  </div>
                </div>
                {(balanceTotalsData.pendingPartnerTopHigh?.length > 0 ||
                  balanceTotalsData.pendingPartnerTopNegative?.length > 0) && (
                  <div className="admin-balance-toplists">
                    <div className="admin-balance-toplist">
                      <h3 className="admin-balance-toplist-title">Top 10 hoogste balance_pending_partner</h3>
                      {balanceTotalsData.pendingPartnerTopHigh?.length > 0 ? (
                        <div className="admin-table-container">
                          <table className="admin-table admin-balance-toplist-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Seller</th>
                                <th>Supplier ID</th>
                                <th style={{ textAlign: 'right' }}>Pending partner</th>
                              </tr>
                            </thead>
                            <tbody>
                              {balanceTotalsData.pendingPartnerTopHigh.map((row, idx) => (
                                <tr key={`hi-${row.supplierId}-${idx}`}>
                                  <td>{idx + 1}</td>
                                  <td>{row.sellerName}</td>
                                  <td>{row.supplierId}</td>
                                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    €{Number(row.balancePendingPartner).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="admin-balance-toplist-empty">Geen rijen met supplier_id.</p>
                      )}
                    </div>
                    <div className="admin-balance-toplist">
                      <h3 className="admin-balance-toplist-title">Top 10 negatiefste balance_pending_partner</h3>
                      {balanceTotalsData.pendingPartnerTopNegative?.length > 0 ? (
                        <div className="admin-table-container">
                          <table className="admin-table admin-balance-toplist-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Seller</th>
                                <th>Supplier ID</th>
                                <th style={{ textAlign: 'right' }}>Pending partner</th>
                              </tr>
                            </thead>
                            <tbody>
                              {balanceTotalsData.pendingPartnerTopNegative.map((row, idx) => (
                                <tr key={`lo-${row.supplierId}-${idx}`}>
                                  <td>{idx + 1}</td>
                                  <td>{row.sellerName}</td>
                                  <td>{row.supplierId}</td>
                                  <td
                                    style={{
                                      textAlign: 'right',
                                      fontVariantNumeric: 'tabular-nums',
                                      color: '#b91c1c',
                                    }}
                                  >
                                    €{Number(row.balancePendingPartner).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="admin-balance-toplist-empty">Geen negatieve pending partner-waarden.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'customers':
        const displayCustomers = searchResults !== null ? searchResults : (customersData?.customers || []);
        const isSearchMode = searchQuery.trim() !== '';
        
        return (
          <div className="admin-section-content">
            <h2>Klanten</h2>
            
            {/* Search Bar */}
            <div className="admin-search-container">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Zoek in alle velden (ID, email, naam, bedrijf, KVK, BTW, IBAN, etc.)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isSearching && <span className="admin-search-loading">Zoeken...</span>}
              {isSearchMode && !isSearching && (
                <span className="admin-search-count">
                  {displayCustomers.length} resultaat{displayCustomers.length !== 1 ? 'en' : ''}
                </span>
              )}
            </div>

            {!isSearchMode && customersData && (
              <div className="admin-stats-grid" style={{ marginTop: '20px' }}>
                <div className="stat-card">
                  <h3>New Model Klanten</h3>
                  <p className="stat-number">{customersData.totalCustomers}</p>
                </div>
                <div className="stat-card">
                  <h3>Nieuwe Klanten</h3>
                  <p className="stat-number">{customersData.newCustomers}</p>
                </div>
                <div className="stat-card">
                  <h3>Bestaande Klanten</h3>
                  <p className="stat-number">{customersData.existingCustomers}</p>
                </div>
                <div className="stat-card">
                  <h3>Met Recurring</h3>
                  <p className="stat-number">{customersData.withRecurring}</p>
                </div>
              </div>
            )}

            {displayCustomers.length > 0 && (
              <div style={{ marginTop: '30px' }}>
                <h3>{isSearchMode ? 'Zoekresultaten' : 'New Model Klanten'}</h3>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Seller ID</th>
                        <th>Email</th>
                        <th>Bedrijf</th>
                        <th>Naam</th>
                        <th>Adres</th>
                        <th>KVK</th>
                        <th>BTW</th>
                        <th>IBAN</th>
                        <th>Type</th>
                        <th>Pakket</th>
                        <th>Billing</th>
                        <th>Recurring</th>
                        <th>Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayCustomers.map((customer) => (
                        <tr 
                          key={customer.id}
                          onClick={() => customer.sellerId && fetchSellerInfo(customer.sellerId)}
                          style={{ 
                            cursor: customer.sellerId ? 'pointer' : 'default',
                            backgroundColor: selectedSeller === customer.sellerId ? '#e3f2fd' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (customer.sellerId) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedSeller !== customer.sellerId) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            } else {
                              e.currentTarget.style.backgroundColor = '#e3f2fd';
                            }
                          }}
                        >
                          <td>{customer.sellerId || '-'}</td>
                          <td>{customer.sellerEmail || '-'}</td>
                          <td>{customer.companyName || '-'}</td>
                          <td>
                            {customer.firstName || customer.lastName 
                              ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                              : '-'
                            }
                          </td>
                          <td>
                            {customer.street || customer.city || customer.postalCode
                              ? `${customer.street || ''}${customer.street && (customer.city || customer.postalCode) ? ', ' : ''}${customer.postalCode || ''} ${customer.city || ''}`.trim()
                              : '-'
                            }
                          </td>
                          <td>{customer.kvkNumber || '-'}</td>
                          <td>{customer.vatNumber || '-'}</td>
                          <td>{customer.iban || '-'}</td>
                          <td>{customer.isNewCustomer ? 'Nieuw' : 'Bestaand'}</td>
                          <td>{customer.package || '-'}</td>
                          <td>{customer.billingPeriod || '-'}</td>
                          <td>{customer.mollieMandateId ? '✓' : '-'}</td>
                          <td>{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('nl-NL') : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isSearchMode && displayCustomers.length === 0 && !isSearching && (
              <div style={{ marginTop: '30px', padding: '20px', textAlign: 'center', color: '#666' }}>
                Geen resultaten gevonden voor "{searchQuery}"
              </div>
            )}

            {!isSearchMode && customersData && (!customersData.customers || customersData.customers.length === 0) && (
              <div style={{ marginTop: '30px', padding: '20px', textAlign: 'center', color: '#666' }}>
                Geen klanten gevonden
              </div>
            )}
          </div>
        );

      case 'sellers':
        const allSellers = sellersData?.sellers || [];
        const sellersFiltered = allSellers.filter((s) => {
          if (sellersStatusFilter === 'enabled') return s.is_active === 1 || s.is_active === true;
          if (sellersStatusFilter === 'disabled') return s.is_active === 0 || s.is_active === false;
          return true;
        });
        const getSellerStatusLabel = (s) => {
          if (s?.is_active !== undefined && s?.is_active !== null) return s.is_active ? 'Enabled' : 'Disabled';
          if (s?.enabled !== undefined && s?.enabled !== null) return s.enabled ? 'Enabled' : 'Disabled';
          if (s?.status != null && s.status !== '') return String(s.status);
          if (s?.supplier_status != null && s.supplier_status !== '') return String(s.supplier_status);
          return '–';
        };
        return (
          <div className="admin-section-content">
            <h2>Sellers</h2>
            <div
              style={{
                marginTop: '0',
                marginBottom: '24px',
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '10px',
                maxWidth: '960px',
                border: '1px solid #cbd5e1',
              }}
            >
              <h3 style={{ marginTop: 0, fontSize: '1.1rem' }}>Sellers — weektrend</h3>
              <p style={{ color: '#475569', fontSize: '14px', lineHeight: 1.5, marginBottom: '14px', maxWidth: '720px' }}>
                <strong>Momentopnames</strong> (totaal / enabled / disabled) worden wekelijks opgeslagen na de sync (cron maandag
                ~04:35 UTC). Vanaf livegang zie je daarin de <strong>ontwikkeling</strong>. Historische enabled/disabled
                hebben we niet; <strong>terugwerkende kracht</strong> toont hoeveel sellers per week bij Magento zijn
                aangemaakt volgens <code>created_at</code> in de huidige database (benadering).
              </p>
              {sellerWeeklyAnalytics?.current && (
                <p style={{ fontSize: '14px', color: '#334155', marginBottom: '12px' }}>
                  <strong>Nu (cache):</strong> {sellerWeeklyAnalytics.current.totalSuppliers} totaal —{' '}
                  <strong>{sellerWeeklyAnalytics.current.enabledCount}</strong> enabled,{' '}
                  <strong>{sellerWeeklyAnalytics.current.disabledCount}</strong> niet-enabled
                </p>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '14px' }}>
                <button
                  type="button"
                  disabled={sellerWeeklySnapshotBusy}
                  onClick={async () => {
                    setSellerWeeklySnapshotBusy(true);
                    try {
                      const res = await authenticatedFetch('/api/admin/seller-weekly-snapshots', { method: 'POST', body: '{}' });
                      const j = await res.json();
                      if (j.success && j.data) {
                        const refresh = await authenticatedFetch('/api/admin/seller-weekly-snapshots');
                        const rj = await refresh.json();
                        if (rj.success && rj.data) setSellerWeeklyAnalytics(rj.data);
                      }
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setSellerWeeklySnapshotBusy(false);
                    }
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: '1px solid #64748b',
                    background: '#fff',
                    cursor: sellerWeeklySnapshotBusy ? 'wait' : 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {sellerWeeklySnapshotBusy ? 'Bezig…' : 'Momentopname deze week opslaan'}
                </button>
              </div>
              {sellerWeeklyAnalytics?.snapshots?.length > 0 && (
                <div style={{ overflowX: 'auto', marginBottom: '18px' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '13px', minWidth: '480px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 10px' }}>Week start (UTC)</th>
                        <th style={{ padding: '8px 10px' }}>Totaal</th>
                        <th style={{ padding: '8px 10px' }}>Enabled</th>
                        <th style={{ padding: '8px 10px' }}>Niet-enabled</th>
                        <th style={{ padding: '8px 10px' }}>Opgeslagen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sellerWeeklyAnalytics.snapshots.slice(0, 24).map((row) => (
                        <tr key={row.weekStart} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{row.weekStartLabel}</td>
                          <td style={{ padding: '8px 10px' }}>{row.totalSuppliers}</td>
                          <td style={{ padding: '8px 10px' }}>{row.enabledCount}</td>
                          <td style={{ padding: '8px 10px' }}>{row.disabledCount}</td>
                          <td style={{ padding: '8px 10px', color: '#64748b', fontSize: '12px' }}>
                            {row.recordedAt ? new Date(row.recordedAt).toLocaleString('nl-NL') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {sellerWeeklyAnalytics?.snapshots?.length === 0 && (
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '12px' }}>
                  Nog geen wekelijkse momentopnames. Draai handmatig hierboven of wacht op de maandag-cron na deploy + migratie.
                </p>
              )}
              {sellerWeeklyAnalytics?.newSellersByMagentoCreatedWeek?.length > 0 && (
                <>
                  <h4 style={{ fontSize: '15px', marginBottom: '8px', color: '#0f172a' }}>
                    Nieuw per week (Magento <code>created_at</code>, retrospectief)
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', flexWrap: 'wrap', minHeight: '120px', marginBottom: '8px' }}>
                    {sellerWeeklyAnalytics.newSellersByMagentoCreatedWeek.slice(-52).map((w) => {
                      const max = Math.max(
                        ...sellerWeeklyAnalytics.newSellersByMagentoCreatedWeek.slice(-52).map((x) => x.newSellerCount),
                        1
                      );
                      const h = Math.max(6, (w.newSellerCount / max) * 72);
                      return (
                        <div
                          key={w.weekStart}
                          title={`${w.weekStartLabel}: ${w.newSellerCount}`}
                          style={{
                            width: '10px',
                            height: `${h}px`,
                            background: '#3b82f6',
                            borderRadius: '2px 2px 0 0',
                            flexShrink: 0,
                          }}
                        />
                      );
                    })}
                  </div>
                  <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                    Laatste {Math.min(52, sellerWeeklyAnalytics.newSellersByMagentoCreatedWeek.length)} weken in de grafiek; hover
                    op balkjes voor week + aantal.
                  </p>
                  <div style={{ overflowX: 'auto', maxHeight: '220px' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          <th style={{ padding: '6px 8px' }}>Week (UTC)</th>
                          <th style={{ padding: '6px 8px' }}>Aantal (created_at)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerWeeklyAnalytics.newSellersByMagentoCreatedWeek.slice(-40).map((w) => (
                          <tr key={w.weekStart} style={{ borderBottom: '1px solid #f8fafc' }}>
                            <td style={{ padding: '6px 8px' }}>{w.weekStartLabel}</td>
                            <td style={{ padding: '6px 8px' }}>{w.newSellerCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
              <p style={{ margin: 0, color: '#666' }}>
                {sellersData
                  ? `${sellersFiltered.length} van ${sellersData.total} seller${sellersData.total !== 1 ? 's' : ''}`
                  : 'Laden...'}
                {sellersData?.source === 'database' && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#059669' }}>(database)</span>
                )}
                {sellersData?.source === 'live' && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#b45309' }}>(live Magento)</span>
                )}
              </p>
              {sellersData?.lastSyncedAt && sellersData?.source === 'database' && (
                <span style={{ fontSize: '0.8rem', color: '#888' }}>
                  Laatste sync: {new Date(sellersData.lastSyncedAt).toLocaleString('nl-NL')}
                </span>
              )}
              <span style={{ fontSize: '0.85rem', color: '#888' }}>
                Standaard uit Postgres (MagentoSupplier). Klik een rij voor details (balance/orders blijven live API).
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.9rem', color: '#666' }}>Filter:</span>
                <button
                  type="button"
                  className={sellersStatusFilter === 'all' ? 'admin-btn primary' : 'admin-btn'}
                  onClick={() => setSellersStatusFilter('all')}
                  style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                >
                  Alles
                </button>
                <button
                  type="button"
                  className={sellersStatusFilter === 'enabled' ? 'admin-btn primary' : 'admin-btn'}
                  onClick={() => setSellersStatusFilter('enabled')}
                  style={{ padding: '6px 12px', fontSize: '0.9rem', color: sellersStatusFilter === 'enabled' ? undefined : '#059669' }}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  className={sellersStatusFilter === 'disabled' ? 'admin-btn primary' : 'admin-btn'}
                  onClick={() => setSellersStatusFilter('disabled')}
                  style={{ padding: '6px 12px', fontSize: '0.9rem', color: sellersStatusFilter === 'disabled' ? undefined : '#dc2626' }}
                >
                  Disabled
                </button>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sellersIncludePackages}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSellersIncludePackages(checked);
                    fetchSectionData('sellers', { includePackages: checked, live: sellersPreferLive });
                  }}
                />
                Packages in lijst tonen (eerste 80 sellers, live supplier/view)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sellersPreferLive}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSellersPreferLive(checked);
                    fetchSectionData('sellers', { includePackages: sellersIncludePackages, live: checked });
                  }}
                />
                Lijst van live Magento (supplier/list) i.p.v. database
              </label>
            </div>
            {loading && allSellers.length === 0 ? (
              <div className="admin-loading">Laden...</div>
            ) : sellersFiltered.length > 0 ? (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Supplier ID</th>
                      <th>Supplier naam</th>
                      <th>Status</th>
                      <th>Packages</th>
                      <th>Verwacht €/maand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellersFiltered.map((seller) => {
                      const sid = seller.supplier_id ?? seller.sellerId;
                      const statusLabel = getSellerStatusLabel(seller);
                      return (
                        <tr
                          key={sid}
                          onClick={() => sid && fetchSellerInfo(sid)}
                          style={{
                            cursor: sid ? 'pointer' : 'default',
                            backgroundColor: selectedSeller === sid ? '#e3f2fd' : 'transparent',
                          }}
                        >
                          <td>{sid || '-'}</td>
                          <td>{seller.supplier_name ?? seller.sellerEmail ?? '-'}</td>
                          <td>
                            {statusLabel === 'Enabled' && <span style={{ color: '#059669', fontWeight: 600 }}>Enabled</span>}
                            {statusLabel === 'Disabled' && <span style={{ color: '#dc2626', fontWeight: 600 }}>Disabled</span>}
                            {statusLabel !== 'Enabled' && statusLabel !== 'Disabled' && statusLabel}
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            {seller.packages && seller.packages.length > 0
                              ? seller.packages.map((p) => getPackageShortLabel(p)).filter(Boolean).join(', ') || '-'
                              : '-'}
                          </td>
                          <td style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                            {seller.packages && seller.packages.length > 0
                              ? `€${getMonthlyRevenueForPackages(seller.packages).toFixed(2)}`
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ marginTop: '1rem', padding: '20px', textAlign: 'center', color: '#666' }}>
                {allSellers.length > 0 && sellersFiltered.length === 0
                  ? 'Geen sellers voor dit filter (probeer “Alles”).'
                  : sellersData?.source === 'database' &&
                      (sellersData?.total === 0 || !sellersData?.sellers?.length)
                    ? 'Geen sellers in de database — draai de Magento-suppliersync (cron of POST /api/admin/sync-magento-suppliers).'
                    : 'Geen sellers gevonden.'}
              </div>
            )}
          </div>
        );

      case 'mailing':
        return renderMailingContent();

      default:
        return <div>Selecteer een sectie</div>;
    }
  };

  const renderMailingContent = () => {
    if (mailingLoading) {
      return <div className="admin-loading">Loading...</div>;
    }

    return (
      <div className="admin-section-content">
        <h2>Mailing</h2>
        
        {/* Mailing Submenu */}
        <div className="admin-submenu">
          <button
            className={`admin-submenu-button ${mailingSubsection === 'consumers' ? 'active' : ''}`}
            onClick={() => setMailingSubsection('consumers')}
          >
            Consumenten
          </button>
          <button
            className={`admin-submenu-button ${mailingSubsection === 'templates' ? 'active' : ''}`}
            onClick={() => setMailingSubsection('templates')}
          >
            Templates
          </button>
          <button
            className={`admin-submenu-button ${mailingSubsection === 'workflows' ? 'active' : ''}`}
            onClick={() => setMailingSubsection('workflows')}
          >
            Workflows
          </button>
          <button
            className={`admin-submenu-button ${mailingSubsection === 'lists' ? 'active' : ''}`}
            onClick={() => {
              setMailingSubsection('lists');
              setBulkAddToListId(null);
              setSelectedConsumers(new Set());
            }}
          >
            Maillijsten
          </button>
          <button
            className={`admin-submenu-button ${mailingSubsection === 'campaigns' ? 'active' : ''}`}
            onClick={() => setMailingSubsection('campaigns')}
          >
            Campagnes
          </button>
          <button
            className={`admin-submenu-button ${mailingSubsection === 'bounces' ? 'active' : ''}`}
            onClick={() => setMailingSubsection('bounces')}
          >
            Bounces
          </button>
        </div>

        {/* Mailing Subsection Content */}
        {mailingSubsection === 'consumers' && renderConsumersContent()}
        {mailingSubsection === 'templates' && renderTemplatesContent()}
        {mailingSubsection === 'workflows' && renderWorkflowsContent()}
        {mailingSubsection === 'lists' && renderListsContent()}
        {mailingSubsection === 'campaigns' && renderCampaignsContent()}
        {mailingSubsection === 'bounces' && renderBouncesContent()}
      </div>
    );
  };

  // Consumer management functions
  const openConsumerEditor = (consumer = null) => {
    if (consumer) {
      setConsumerForm({
        firstName: consumer.firstName || '',
        lastName: consumer.lastName || '',
        email: consumer.email || '',
        store: consumer.store || 'NL',
        country: consumer.country || '',
        phone: consumer.phone || '',
        source: consumer.source || 'manual',
        sourceUrl: consumer.sourceUrl || ''
      });
      setEditingConsumer(consumer.id);
    } else {
      setConsumerForm({
        firstName: '',
        lastName: '',
        email: '',
        store: 'NL',
        country: '',
        phone: '',
        source: 'manual',
        sourceUrl: ''
      });
      setEditingConsumer('new');
    }
  };

  const closeConsumerEditor = () => {
    setEditingConsumer(null);
    setConsumerForm({
      firstName: '',
      lastName: '',
      email: '',
      store: 'NL',
      country: '',
      phone: '',
      source: 'manual',
      sourceUrl: ''
    });
  };

  const saveConsumer = async () => {
    if (!consumerForm.firstName || !consumerForm.lastName || !consumerForm.email || !consumerForm.store) {
      alert('Voornaam, achternaam, email en store zijn verplicht');
      return;
    }

    setConsumerLoading(true);
    try {
      const url = '/api/admin/mailing/consumers';
      const method = editingConsumer === 'new' ? 'POST' : 'PUT';
      const body = editingConsumer === 'new' 
        ? consumerForm 
        : { ...consumerForm, id: editingConsumer };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(editingConsumer === 'new' ? 'Consument aangemaakt!' : 'Consument bijgewerkt!');
        closeConsumerEditor();
        fetchMailingData('consumers', consumerSearchQuery, consumerStoreFilter, consumerCountryFilter, consumerUnsubscribedFilter);
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving consumer:', error);
      alert('Error saving consumer: ' + error.message);
    } finally {
      setConsumerLoading(false);
    }
  };

  // Handle search with debounce
  const handleConsumerSearch = (query) => {
    setConsumerSearchQuery(query);
    
    // Clear existing timeout
    if (consumerSearchTimeout) {
      clearTimeout(consumerSearchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      fetchMailingData('consumers', query, consumerStoreFilter, consumerCountryFilter, consumerUnsubscribedFilter);
    }, 500); // 500ms debounce
    
    setConsumerSearchTimeout(timeout);
  };

  // Handle store filter change
  const handleStoreFilterChange = (store) => {
    setConsumerStoreFilter(store);
    fetchMailingData('consumers', consumerSearchQuery, store, consumerCountryFilter, consumerUnsubscribedFilter);
  };

  // Handle country filter change
  const handleCountryFilterChange = (country) => {
    setConsumerCountryFilter(country);
    fetchMailingData('consumers', consumerSearchQuery, consumerStoreFilter, country, consumerUnsubscribedFilter);
  };

  // Handle unsubscribed filter change
  const handleUnsubscribedFilterChange = (unsubscribed) => {
    setConsumerUnsubscribedFilter(unsubscribed);
    fetchMailingData('consumers', consumerSearchQuery, consumerStoreFilter, consumerCountryFilter, unsubscribed);
  };

  // Clear all filters
  const clearConsumerFilters = () => {
    setConsumerSearchQuery('');
    setConsumerStoreFilter('');
    setConsumerCountryFilter('');
    setConsumerUnsubscribedFilter('');
    fetchMailingData('consumers');
  };

  const renderConsumersContent = () => {
    const consumers = mailingData.consumers?.consumers || [];
    const pagination = mailingData.consumers?.pagination;

    // If editing, show editor
    if (editingConsumer) {
      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingConsumer === 'new' ? 'Nieuwe Consument' : 'Consument Bewerken'}</h3>
            <button 
              className="admin-button-secondary"
              onClick={closeConsumerEditor}
            >
              Annuleren
            </button>
          </div>

          <div style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Voornaam *
                </label>
                <input
                  type="text"
                  value={consumerForm.firstName}
                  onChange={(e) => setConsumerForm({ ...consumerForm, firstName: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="Jan"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Achternaam *
                </label>
                <input
                  type="text"
                  value={consumerForm.lastName}
                  onChange={(e) => setConsumerForm({ ...consumerForm, lastName: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="Jansen"
                />
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Email *
              </label>
              <input
                type="email"
                value={consumerForm.email}
                onChange={(e) => setConsumerForm({ ...consumerForm, email: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="jan.jansen@example.com"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Store *
                </label>
                <select
                  value={consumerForm.store}
                  onChange={(e) => setConsumerForm({ ...consumerForm, store: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="NL">NL</option>
                  <option value="DE">DE</option>
                  <option value="BE">BE</option>
                  <option value="FR">FR</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Land
                </label>
                <input
                  type="text"
                  value={consumerForm.country}
                  onChange={(e) => setConsumerForm({ ...consumerForm, country: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="Nederland"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Telefoon
                </label>
                <input
                  type="tel"
                  value={consumerForm.phone}
                  onChange={(e) => setConsumerForm({ ...consumerForm, phone: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="+31 6 12345678"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Bron
                </label>
                <select
                  value={consumerForm.source}
                  onChange={(e) => setConsumerForm({ ...consumerForm, source: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="manual">Handmatig</option>
                  <option value="magento_sync">Magento Sync</option>
                  <option value="import">Import</option>
                  <option value="api">API</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Bron URL
              </label>
              <input
                type="text"
                value={consumerForm.sourceUrl}
                onChange={(e) => setConsumerForm({ ...consumerForm, sourceUrl: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="https://..."
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="admin-button-primary"
                onClick={saveConsumer}
                disabled={consumerLoading}
              >
                {consumerLoading ? 'Opslaan...' : editingConsumer === 'new' ? 'Consument Aanmaken' : 'Consument Opslaan'}
              </button>
              <button
                className="admin-button-secondary"
                onClick={closeConsumerEditor}
                disabled={consumerLoading}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Consumenten</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {syncStatus?.isRunning ? (
              <button 
                className="admin-button-secondary"
                onClick={stopSync}
                style={{ backgroundColor: '#f44336', color: 'white' }}
              >
                ⏹️ Stop Sync
              </button>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', color: '#666' }}>Start vanaf Entity ID (optioneel):</label>
                  <input
                    type="number"
                    value={startEntityId}
                    onChange={(e) => setStartEntityId(e.target.value)}
                    placeholder="Bijv. 50000"
                    style={{ 
                      padding: '6px 10px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      width: '120px',
                      fontSize: '14px'
                    }}
                    disabled={syncLoading}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', color: '#666' }}>Max Pagina's (leeg = oneindig):</label>
                  <input
                    type="number"
                    value={maxPages}
                    onChange={(e) => setMaxPages(e.target.value)}
                    placeholder="Bijv. 10"
                    style={{ 
                      padding: '6px 10px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      width: '120px',
                      fontSize: '14px'
                    }}
                    disabled={syncLoading}
                    title="Aantal pagina's om te verwerken (500 orders per pagina). Laat leeg voor oneindig."
                  />
                </div>
                <button 
                  className="admin-button-secondary"
                  onClick={() => startSync(false, false)}
                  disabled={syncLoading}
                  title={
                    syncStatus?.checkpoint 
                      ? `Herstart vanaf pagina ${syncStatus.checkpoint.lastPage + 1}` 
                      : startEntityId 
                        ? `Start vanaf entity ID ${startEntityId}${maxPages ? ` (max ${maxPages} pagina's)` : ''}` 
                        : maxPages 
                          ? `Start sync (max ${maxPages} pagina's = ${maxPages * 500} orders)` 
                          : 'Start sync vanaf het begin'
                  }
                >
                  {syncLoading ? 'Starten...' : '🔄 Start Sync' + 
                    (syncStatus?.checkpoint ? ' (Hervatten)' : 
                     startEntityId ? ` (Vanaf ${startEntityId})` : 
                     maxPages ? ` (${maxPages} p.)` : '')}
                </button>
                <button 
                  className="admin-button-secondary"
                  onClick={() => startSync(false, true)}
                  disabled={syncLoading}
                  style={{ backgroundColor: '#f44336', color: 'white' }}
                  title="Start sync vanaf het begin (negeer checkpoint)"
                >
                  {syncLoading ? 'Starten...' : '🔄 Start Sync (Vanaf Begin)'}
                </button>
                <button 
                  className="admin-button-secondary"
                  onClick={() => startSync(true)}
                  disabled={syncLoading}
                  style={{ backgroundColor: '#ff9800', color: 'white' }}
                  title="Test import: maximaal 500 orders (1 pagina)"
                >
                  {syncLoading ? 'Testen...' : '🧪 Test Import'}
                </button>
              </>
            )}
            <button 
              className="admin-button-primary"
              onClick={() => openConsumerEditor()}
            >
              + Nieuwe Consument
            </button>
          </div>
        </div>

        {/* Sync Status */}
        {(syncStatus && (syncStatus.isRunning || syncStatus.progress?.totalProcessed > 0 || syncStatus.checkpoint)) && (
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            backgroundColor: syncStatus.isRunning ? '#e3f2fd' : '#e8f5e9',
            borderRadius: '8px',
            border: `2px solid ${syncStatus.isRunning ? '#2196f3' : '#4caf50'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ margin: 0, fontSize: '18px' }}>
                {syncStatus.isRunning ? '🔄 Sync Loopt...' : syncStatus.checkpoint ? '⏸️ Sync Gepauzeerd' : '✅ Sync Voltooid'}
              </h4>
              {syncStatus.startedAt && (
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Gestart: {new Date(syncStatus.startedAt).toLocaleString('nl-NL')}
                  {syncStatus.duration && (
                    <> • Duur: {Math.round(syncStatus.duration / 1000 / 60)} min</>
                  )}
                </span>
              )}
            </div>
            
            {syncStatus.progress && (
              <div>
                {/* Progress Info */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                    <span><strong>Verwerkt:</strong> {(syncStatus.progress.totalProcessed || syncStatus.progress.processed || 0).toLocaleString('nl-NL')} orders</span>
                    <span><strong>Huidige pagina:</strong> {syncStatus.progress.currentPage || 0}</span>
                  </div>
                  {syncStatus.checkpoint && (
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                      📍 Checkpoint: Laatste entity_id {syncStatus.checkpoint.lastEntityId} (pagina {syncStatus.checkpoint.lastPage})
                    </div>
                  )}
                </div>
                
                {/* Statistics Grid */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                  gap: '15px', 
                  fontSize: '14px',
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '6px'
                }}>
                  <div style={{ color: '#4caf50', fontWeight: 'bold' }}>
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                      {(syncStatus.progress.totalCreated || syncStatus.progress.created || 0).toLocaleString('nl-NL')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>✅ Aangemaakt</div>
                  </div>
                  <div style={{ color: '#ff9800', fontWeight: 'bold' }}>
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                      {(syncStatus.progress.totalSkipped || 0).toLocaleString('nl-NL')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>⏭️ Geskipt (duplicaten)</div>
                  </div>
                  {(syncStatus.progress.totalErrors || syncStatus.progress.errors || 0) > 0 && (
                    <div style={{ color: '#f44336', fontWeight: 'bold' }}>
                      <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                        {(syncStatus.progress.totalErrors || syncStatus.progress.errors || 0).toLocaleString('nl-NL')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>❌ Fouten</div>
                    </div>
                  )}
                  {syncStatus.progress.lastEntityId > 0 && (
                    <div style={{ color: '#2196f3', fontWeight: 'bold' }}>
                      <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                        {syncStatus.progress.lastEntityId.toLocaleString('nl-NL')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>📍 Laatste Entity ID</div>
                    </div>
                  )}
                </div>
                
                {syncStatus.checkpoint && !syncStatus.isRunning && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '10px', 
                    backgroundColor: '#fff3cd', 
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#856404'
                  }}>
                    💡 <strong>Tip:</strong> De sync kan worden hervat vanaf pagina {syncStatus.checkpoint.lastPage + 1} door opnieuw op "Start Sync" te klikken.
                  </div>
                )}

                {syncStatus.completedAt && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    Voltooid: {new Date(syncStatus.completedAt).toLocaleString('nl-NL')}
                    {syncStatus.duration && (
                      <span> (Duur: {Math.round(syncStatus.duration / 1000 / 60)} minuten)</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Search and filters */}
        <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={consumerSearchQuery}
            onChange={(e) => handleConsumerSearch(e.target.value)}
            placeholder="Zoeken op naam of email..."
            style={{
              flex: '1',
              minWidth: '200px',
              maxWidth: '400px',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          
          <select
            value={consumerStoreFilter}
            onChange={(e) => handleStoreFilterChange(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Alle Stores</option>
            <option value="NL">NL</option>
            <option value="DE">DE</option>
            <option value="BE">BE</option>
            <option value="FR">FR</option>
          </select>
          
          <select
            value={consumerCountryFilter}
            onChange={(e) => handleCountryFilterChange(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Alle Landen</option>
            <option value="NL">Nederland</option>
            <option value="DE">Duitsland</option>
            <option value="BE">België</option>
            <option value="FR">Frankrijk</option>
          </select>
          
          <select
            value={consumerUnsubscribedFilter}
            onChange={(e) => handleUnsubscribedFilterChange(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Alle Status</option>
            <option value="false">Niet Uitgeschreven</option>
            <option value="true">Uitgeschreven</option>
          </select>
          
          {(consumerSearchQuery || consumerStoreFilter || consumerCountryFilter || consumerUnsubscribedFilter) && (
            <>
              <button
                onClick={clearConsumerFilters}
                style={{
                  padding: '10px 15px',
                  backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Filters Wissen
            </button>
              <button
                onClick={() => {
                  const hasFilters = consumerSearchQuery || consumerStoreFilter || consumerCountryFilter || consumerUnsubscribedFilter;
                  if (!hasFilters) {
                    alert('Selecteer eerst filters om bulk toevoegen te gebruiken');
                    return;
                  }
                  // Show list selection
                  const lists = mailingData.lists || [];
                  if (lists.length === 0) {
                    alert('Maak eerst een lijst aan voordat je consumenten kunt toevoegen');
                    return;
                  }
                  const listOptions = lists.map((l, i) => `${i + 1}. ${l.name}`).join('\n');
                  const selectedListId = prompt(
                    `Voeg alle gefilterde consumenten toe aan een lijst.\n\nBeschikbare lijsten:\n${listOptions}\n\nVoer het nummer in:`
                  );
                  if (selectedListId) {
                    const listIndex = parseInt(selectedListId) - 1;
                    const selectedList = lists[listIndex];
                    if (selectedList) {
                      addFilteredConsumersToList(selectedList.id);
                    } else {
                      alert('Ongeldig lijst nummer');
                    }
                  }
                }}
                style={{
                  padding: '10px 15px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                📦 Voeg Alle Gefilterde Toe
              </button>
            </>
          )}
        </div>

        {/* Show active filters */}
        {(consumerSearchQuery || consumerStoreFilter || consumerCountryFilter || consumerUnsubscribedFilter) && (
          <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
            Actieve filters:
            {consumerSearchQuery && <span style={{ marginLeft: '5px', padding: '2px 8px', backgroundColor: '#e3f2fd', borderRadius: '3px' }}>Zoek: "{consumerSearchQuery}"</span>}
            {consumerStoreFilter && <span style={{ marginLeft: '5px', padding: '2px 8px', backgroundColor: '#e3f2fd', borderRadius: '3px' }}>Store: {consumerStoreFilter}</span>}
            {consumerCountryFilter && <span style={{ marginLeft: '5px', padding: '2px 8px', backgroundColor: '#e3f2fd', borderRadius: '3px' }}>Land: {consumerCountryFilter}</span>}
            {consumerUnsubscribedFilter && <span style={{ marginLeft: '5px', padding: '2px 8px', backgroundColor: '#e3f2fd', borderRadius: '3px' }}>Status: {consumerUnsubscribedFilter === 'true' ? 'Uitgeschreven' : 'Niet Uitgeschreven'}</span>}
          </div>
        )}

        {pagination && (
          <div style={{ marginBottom: '15px', color: '#666' }}>
            Totaal: {pagination.total} consumenten
            {consumerSearchQuery && <span> (gefilterd)</span>}
          </div>
        )}

        {/* Bulk actions toolbar */}
        {selectedConsumers.size > 0 && (
          <div style={{
            marginBottom: '15px',
            padding: '15px',
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <strong>{selectedConsumers.size}</strong> consument(en) geselecteerd
              {bulkAddToListId && (() => {
                const selectedList = (mailingData.lists || []).find(l => l.id === bulkAddToListId);
                return selectedList ? (
                  <span style={{ marginLeft: '10px', color: '#2196f3', fontWeight: 'bold' }}>
                    → Toevoegen aan: {selectedList.name}
                  </span>
                ) : null;
              })()}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {!bulkAddToListId && (
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      setBulkAddToListId(e.target.value);
                    }
                  }}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Selecteer lijst...</option>
                  {(mailingData.lists || []).map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name} ({list.totalConsumers || 0} leden)
                    </option>
                  ))}
                </select>
              )}
              {bulkAddToListId && (
                <>
                  <button
                    className="admin-button-primary"
                    onClick={() => {
                      addMembersToList(bulkAddToListId, selectedConsumers);
                    }}
                  >
                    ✅ Toevoegen aan Lijst
                  </button>
                  <button
                    className="admin-button-secondary"
                    onClick={() => setBulkAddToListId(null)}
                  >
                    Lijst Wijzigen
                  </button>
                </>
              )}
              {mailingData.lists && mailingData.lists.length === 0 && !bulkAddToListId && (
                <span style={{ color: '#666', fontSize: '12px' }}>
                  Geen lijsten beschikbaar. Maak eerst een lijst aan.
                </span>
              )}
              <button
                className="admin-button-secondary"
                onClick={() => {
                  setSelectedConsumers(new Set());
                  setBulkAddToListId(null);
                }}
              >
                Deselecteren
              </button>
            </div>
          </div>
        )}

        {consumers.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedConsumers.size === consumers.length && consumers.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedConsumers(new Set(consumers.map(c => c.id)));
                        } else {
                          setSelectedConsumers(new Set());
                        }
                      }}
                    />
                  </th>
                  <th>Naam</th>
                  <th>Email</th>
                  <th>Store</th>
                  <th>Land</th>
                  <th>Uitgeschreven</th>
                  <th>Laatste Contact</th>
                  <th>Emails Verstuurd</th>
                  <th>Emails Geopend</th>
                  <th>Emails Geklikt</th>
                  <th>Datum</th>
                </tr>
              </thead>
              <tbody>
                {consumers.map((consumer) => (
                  <tr key={consumer.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedConsumers.has(consumer.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedConsumers);
                          if (e.target.checked) {
                            newSelected.add(consumer.id);
                          } else {
                            newSelected.delete(consumer.id);
                          }
                          setSelectedConsumers(newSelected);
                        }}
                      />
                    </td>
                    <td>{consumer.firstName} {consumer.lastName}</td>
                    <td>{consumer.email}</td>
                    <td>{consumer.store}</td>
                    <td>{consumer.country || '-'}</td>
                    <td>{consumer.isUnsubscribed ? '✓' : '-'}</td>
                    <td>{consumer.lastContactAt ? new Date(consumer.lastContactAt).toLocaleDateString('nl-NL') : '-'}</td>
                    <td>{consumer.totalEmailsSent}</td>
                    <td>{consumer.totalEmailsOpened}</td>
                    <td>{consumer.totalEmailsClicked}</td>
                    <td>{new Date(consumer.createdAt).toLocaleDateString('nl-NL')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Geen consumenten gevonden
          </div>
        )}
      </div>
    );
  };

  const insertVariable = (variableName) => {
    const variable = `{{${variableName}}}`;
    const textarea = document.getElementById('htmlContent');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateForm.htmlContent;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setTemplateForm({ ...templateForm, htmlContent: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const insertVariableInSubject = (variableName) => {
    const variable = `{{${variableName}}}`;
    const textarea = document.getElementById('subject');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateForm.subject;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setTemplateForm({ ...templateForm, subject: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  // Helper function to wrap content in email template (same as backend)
  const wrapEmailTemplate = (content) => {
    if (!content) return '';
    
    // Check if content already has full HTML structure
    if (content.trim().toLowerCase().startsWith('<!doctype') || 
        (content.includes('<html') && content.includes('</html>'))) {
      return content; // Already a complete HTML document
    }

    // Wrap in a responsive email template with inline styles
    return `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>Email Template</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f4f4f4; line-height: 1.6; color: #333333;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f4f4f4;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <!-- Header -->
          <tr>
            <td style="padding: 30px 40px; background-color: #ffffff; border-bottom: 1px solid #eeeeee;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td style="vertical-align: middle;">
                    <img src="/images/logo_sdeal_navbar.svg" alt="SDeal Logo" style="height: 40px; width: auto; display: block;" />
                  </td>
                  <td style="text-align: right; vertical-align: middle;">
                    <img src="/images/smiley_sdeal.png" alt="SDeal" style="height: 40px; width: auto; display: block; margin-left: auto;" />
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Content -->
          <tr>
            <td style="padding: 40px;">
              ${content}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 30px 40px; background-color: #f9f9f9; border-top: 1px solid #eeeeee; text-align: center; font-size: 12px; color: #666666;">
              <p style="margin: 0 0 15px 0; font-size: 14px; color: #333333;">
                Met vriendelijke groet,<br>
                <strong>Het SDeal Team</strong>
              </p>
              <p style="margin: 0 0 20px 0;">
                <img src="/images/smiley_sdeal.png" alt="SDeal" style="height: 50px; width: auto; display: block; margin: 0 auto;" />
              </p>
              <p style="margin: 0 0 10px 0;">
                <strong>SDeal B.V.</strong><br>
                Osloweg 110, 9723 BX Groningen, The Netherlands<br>
                KVK: 76103080 | VAT: NL 860508468B01
              </p>
              <p style="margin: 10px 0 0 0;">
                <a href="https://www.sdeal.com" style="color: #0066cc; text-decoration: none;">www.sdeal.com</a> | 
                <a href="mailto:info@sdeal.com" style="color: #0066cc; text-decoration: none;">info@sdeal.com</a>
              </p>
              <p style="margin: 20px 0 0 0; font-size: 11px; color: #999999;">
                <a href="{{unsubscribeUrl}}" style="color: #999999; text-decoration: underline;">Afmelden</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const generatePreview = () => {
    let preview = templateForm.htmlContent || '';
    // Replace variables with example values
    availableVariables.forEach(variable => {
      const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
      preview = preview.replace(regex, variable.example);
    });
    
    // Wrap in email template
    preview = wrapEmailTemplate(preview);
    
    // Replace unsubscribe URL and email in footer
    preview = preview.replace(/\{\{unsubscribeUrl\}\}/g, '#');
    preview = preview.replace(/\{\{email\}\}/g, 'voorbeeld@email.com');
    
    // Replace subject variables too
    let subjectPreview = templateForm.subject || '';
    availableVariables.forEach(variable => {
      const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
      subjectPreview = subjectPreview.replace(regex, variable.example);
    });
    setTemplatePreview(preview);
  };

  const openTemplateEditor = (template = null) => {
    if (template) {
      setTemplateForm({
        name: template.name || '',
        subject: template.subject || '',
        htmlContent: template.htmlContent || '',
        textContent: template.textContent || '',
        category: template.category || '',
        isActive: template.isActive !== undefined ? template.isActive : true,
        availableVariables: template.availableVariables || []
      });
      setEditingTemplate(template.id);
    } else {
      setTemplateForm({
        name: '',
        subject: '',
        htmlContent: '',
        textContent: '',
        category: '',
        isActive: true,
        availableVariables: []
      });
      setEditingTemplate('new');
    }
    generatePreview();
  };

  const closeTemplateEditor = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      htmlContent: '',
      textContent: '',
      category: '',
      isActive: true,
      availableVariables: []
    });
    setTemplatePreview('');
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.htmlContent) {
      alert('Naam, onderwerp en HTML content zijn verplicht');
      return;
    }

    setTemplateLoading(true);
    try {
      const url = '/api/admin/mailing/templates';
      const method = editingTemplate === 'new' ? 'POST' : 'PUT';
      const body = editingTemplate === 'new' 
        ? templateForm 
        : { ...templateForm, id: editingTemplate };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (result.success) {
        alert(editingTemplate === 'new' ? 'Template aangemaakt!' : 'Template bijgewerkt!');
        closeTemplateEditor();
        fetchMailingData('templates');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template: ' + error.message);
    } finally {
      setTemplateLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Weet je zeker dat je dit template wilt verwijderen?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/mailing/templates?id=${templateId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        alert('Template verwijderd!');
        fetchMailingData('templates');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template: ' + error.message);
    }
  };

  const renderTemplatesContent = () => {
    const templates = mailingData.templates || [];

    // If editing, show editor
    if (editingTemplate) {
      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingTemplate === 'new' ? 'Nieuw Template' : 'Template Bewerken'}</h3>
            <button 
              className="admin-button-secondary"
              onClick={closeTemplateEditor}
            >
              Annuleren
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Left Column: Form */}
            <div>
              <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Beschikbare Variabelen</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {availableVariables.map((variable) => (
                    <button
                      key={variable.name}
                      type="button"
                      onClick={() => insertVariable(variable.name)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                      title={variable.description}
                    >
                      {variable.label} ({`{{${variable.name}}}`})
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                  Klik op een variabele om deze in te voegen in de HTML content
                </div>
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '12px' }}>
                  <strong>💡 Links toevoegen:</strong> Gebruik <code style={{ background: '#fff', padding: '2px 4px', borderRadius: '2px' }}>&lt;a href="https://www.sdeal.com" style="color: #0066cc; text-decoration: none;"&gt;Link tekst&lt;/a&gt;</code> voor klikbare links in je email.
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Template Naam *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="Bijv: Welcome Email"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Onderwerp *
                  <span style={{ marginLeft: '10px', fontSize: '12px', fontWeight: 'normal' }}>
                    {availableVariables.map((v) => (
                      <button
                        key={v.name}
                        type="button"
                        onClick={() => insertVariableInSubject(v.name)}
                        style={{
                          marginLeft: '5px',
                          padding: '2px 6px',
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                        title={v.description}
                      >
                        {v.name}
                      </button>
                    ))}
                  </span>
                </label>
                <input
                  id="subject"
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => {
                    setTemplateForm({ ...templateForm, subject: e.target.value });
                    generatePreview();
                  }}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="Bijv: Welkom {{firstName}}!"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={{ fontWeight: 'bold' }}>
                    HTML Content *
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const basicTemplate = `<h2 style="color: #333333; font-size: 24px; margin: 0 0 20px 0;">Welkom {{firstName}}!</h2>
<p style="color: #666666; font-size: 16px; margin: 0 0 15px 0; line-height: 1.6;">
  Bedankt voor je aanmelding bij SDeal. We zijn blij je te verwelkomen!
</p>
<p style="color: #666666; font-size: 16px; margin: 0 0 15px 0; line-height: 1.6;">
  Je kunt nu gebruik maken van alle voordelen die SDeal te bieden heeft.
</p>
<p style="color: #666666; font-size: 16px; margin: 0 0 20px 0; line-height: 1.6;">
  <a href="https://www.sdeal.com" style="display: inline-block; padding: 12px 24px; background-color: #2196F3; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: 600;">Bezoek onze website</a>
</p>
<p style="color: #666666; font-size: 16px; margin: 0; line-height: 1.6;">
  Met vriendelijke groet,<br>
  <strong>Het SDeal Team</strong>
</p>`;
                      setTemplateForm({ ...templateForm, htmlContent: basicTemplate });
                      generatePreview();
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Laad Basis Template
                  </button>
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', padding: '10px', backgroundColor: '#f0f7ff', borderRadius: '4px' }}>
                  <strong>Tips:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Je hoeft alleen de content te schrijven. De email wordt automatisch gewrapped in een mooie template.</li>
                    <li>Gebruik inline styles voor opmaak (bijv. <code style={{ background: '#e0e0e0', padding: '2px 4px', borderRadius: '2px' }}>style="color: #333; font-size: 16px;"</code>).</li>
                    <li>Maak klikbare links met: <code style={{ background: '#e0e0e0', padding: '2px 4px', borderRadius: '2px' }}>&lt;a href="https://www.sdeal.com" style="color: #0066cc; text-decoration: none;"&gt;Link tekst&lt;/a&gt;</code></li>
                    <li>Voor buttons: <code style={{ background: '#e0e0e0', padding: '2px 4px', borderRadius: '2px' }}>&lt;a href="URL" style="display: inline-block; padding: 12px 24px; background-color: #2196F3; color: #ffffff; text-decoration: none; border-radius: 4px;"&gt;Button tekst&lt;/a&gt;</code></li>
                  </ul>
                </div>
                <textarea
                  id="htmlContent"
                  value={templateForm.htmlContent}
                  onChange={(e) => {
                    setTemplateForm({ ...templateForm, htmlContent: e.target.value });
                    generatePreview();
                  }}
                  style={{ 
                    width: '100%', 
                    minHeight: '300px', 
                    padding: '8px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}
                  placeholder='<h2 style="color: #333; font-size: 24px;">Welkom {{firstName}}!</h2>\n<p style="color: #666; font-size: 16px;">Je content hier...</p>'
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Text Content (Optioneel)
                </label>
                <textarea
                  value={templateForm.textContent}
                  onChange={(e) => setTemplateForm({ ...templateForm, textContent: e.target.value })}
                  style={{ 
                    width: '100%', 
                    minHeight: '150px', 
                    padding: '8px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}
                  placeholder="Plain text versie van de email"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Categorie
                  </label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Geen categorie</option>
                    <option value="welcome">Welcome</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="transactional">Transactioneel</option>
                    <option value="promotional">Promotie</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Status
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={templateForm.isActive}
                      onChange={(e) => setTemplateForm({ ...templateForm, isActive: e.target.checked })}
                    />
                    <span>Actief</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="admin-button-primary"
                  onClick={saveTemplate}
                  disabled={templateLoading}
                >
                  {templateLoading ? 'Opslaan...' : editingTemplate === 'new' ? 'Template Aanmaken' : 'Template Opslaan'}
                </button>
                <button
                  className="admin-button-secondary"
                  onClick={closeTemplateEditor}
                  disabled={templateLoading}
                >
                  Annuleren
                </button>
              </div>
            </div>

            {/* Right Column: Preview */}
            <div>
              <h4 style={{ marginBottom: '15px' }}>Preview</h4>
              <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                padding: '20px',
                backgroundColor: 'white',
                minHeight: '400px'
              }}>
                <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
                  <strong>Onderwerp:</strong> {templateForm.subject || '(geen onderwerp)'}
                </div>
                <div 
                  dangerouslySetInnerHTML={{ __html: templatePreview || '<p style="color: #999; padding: 20px;">Geen preview beschikbaar. Voeg HTML content toe om een preview te zien.</p>' }}
                  style={{ 
                    lineHeight: '1.6',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '0',
                    backgroundColor: '#f4f4f4',
                    minHeight: '400px',
                    overflow: 'auto'
                  }}
                />
              </div>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px', fontSize: '12px' }}>
                💡 <strong>Tip:</strong> De preview toont voorbeeldwaarden voor variabelen. In echte emails worden deze vervangen door echte consument data.
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show template list
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Email Templates</h3>
          <button 
            className="admin-button-primary"
            onClick={() => openTemplateEditor()}
          >
            + Nieuw Template
          </button>
        </div>

        {templates.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Categorie</th>
                  <th>Onderwerp</th>
                  <th>Actief</th>
                  <th>Campagnes</th>
                  <th>Workflows</th>
                  <th>Datum</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td>{template.category || '-'}</td>
                    <td>{template.subject}</td>
                    <td>{template.isActive ? '✓' : '✗'}</td>
                    <td>{template._count?.campaigns || 0}</td>
                    <td>{template._count?.workflowSteps || 0}</td>
                    <td>{new Date(template.createdAt).toLocaleDateString('nl-NL')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button 
                          className="admin-button-small"
                          onClick={() => openTemplateEditor(template)}
                        >
                          Bewerken
                        </button>
                        <button 
                          className="admin-button-small"
                          onClick={() => deleteTemplate(template.id)}
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                        >
                          Verwijderen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Geen templates gevonden. Klik op "Nieuw Template" om er een aan te maken.
          </div>
        )}
      </div>
    );
  };

  // Workflow trigger types
  const triggerTypes = [
    { value: 'consumer_created', label: 'Nieuwe Consument', description: 'Wanneer een nieuwe consument wordt aangemaakt' },
    { value: 'email_opened', label: 'Email Geopend', description: 'Wanneer een consument een email opent' },
    { value: 'email_clicked', label: 'Email Geklikt', description: 'Wanneer een consument op een link in een email klikt' },
    { value: 'payment_completed', label: 'Betaling Voltooid', description: 'Wanneer een betaling is voltooid' },
    { value: 'consumer_signup', label: 'Consument Aangemeld', description: 'Wanneer een consument zich aanmeldt' },
    { value: 'consumer_purchase', label: 'Consument Koopt', description: 'Wanneer een consument een aankoop doet' },
    { value: 'date_based', label: 'Datum Gebaseerd', description: 'Op basis van een specifieke datum' },
    { value: 'manual', label: 'Handmatig', description: 'Alleen handmatig te activeren' }
  ];

  // Workflow action types
  const actionTypes = [
    { value: 'send_email', label: 'Stuur Email', description: 'Stuur een email naar de consument', requiresTemplate: true },
    { value: 'add_to_list', label: 'Voeg Toe aan Lijst', description: 'Voeg consument toe aan een maillijst', requiresList: true },
    { value: 'remove_from_list', label: 'Verwijder uit Lijst', description: 'Verwijder consument uit een maillijst', requiresList: true },
    { value: 'update_field', label: 'Update Veld', description: 'Update een veld van de consument', requiresField: true }
  ];

  const addWorkflowStep = () => {
    setWorkflowForm(prev => ({
      ...prev,
      steps: [...prev.steps, {
        actionType: 'send_email',
        templateId: '',
        listId: '',
        fieldName: '',
        fieldValue: '',
        delayDays: 0,
        delayHours: 0,
        delayMinutes: 0,
        conditions: {},
        isActive: true
      }]
    }));
  };

  const removeWorkflowStep = (index) => {
    setWorkflowForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const updateWorkflowStep = (index, field, value) => {
    setWorkflowForm(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const openWorkflowEditor = (workflow = null) => {
    // Ensure templates and lists are loaded
    if (!mailingData.templates) fetchMailingData('templates');
    if (!mailingData.lists) fetchMailingData('lists');
    
    if (workflow) {
      setWorkflowForm({
        name: workflow.name || '',
        description: workflow.description || '',
        triggerType: workflow.triggerType || 'consumer_created',
        triggerConditions: workflow.triggerConditions || {},
        isActive: workflow.isActive !== undefined ? workflow.isActive : true,
        steps: workflow.steps ? workflow.steps.map(step => ({
          actionType: step.actionType || 'send_email',
          templateId: step.templateId || '',
          listId: step.actionConfig?.listId || '',
          fieldName: step.actionConfig?.fieldName || '',
          fieldValue: step.actionConfig?.fieldValue || '',
          delayDays: step.delayDays || 0,
          delayHours: step.delayHours || 0,
          delayMinutes: step.delayMinutes || 0,
          conditions: step.conditions || {},
          isActive: step.isActive !== undefined ? step.isActive : true
        })) : []
      });
      setEditingWorkflow(workflow.id);
    } else {
      setWorkflowForm({
        name: '',
        description: '',
        triggerType: 'consumer_created',
        triggerConditions: {},
        isActive: true,
        steps: []
      });
      setEditingWorkflow('new');
    }
  };

  const closeWorkflowEditor = () => {
    setEditingWorkflow(null);
    setWorkflowForm({
      name: '',
      description: '',
      triggerType: 'consumer_created',
      triggerConditions: {},
      isActive: true,
      steps: []
    });
  };

  const saveWorkflow = async () => {
    if (!workflowForm.name || !workflowForm.triggerType) {
      alert('Naam en trigger type zijn verplicht');
      return;
    }

    setWorkflowLoading(true);
    try {
      const url = '/api/admin/mailing/workflows';
      const method = editingWorkflow === 'new' ? 'POST' : 'PUT';
      
      const steps = workflowForm.steps.map((step, index) => {
        const stepData = {
          stepOrder: index + 1,
          actionType: step.actionType,
          delayDays: parseInt(step.delayDays) || 0,
          delayHours: parseInt(step.delayHours) || 0,
          delayMinutes: parseInt(step.delayMinutes) || 0,
          isActive: step.isActive,
          conditions: step.conditions || {}
        };

        if (step.actionType === 'send_email' && step.templateId) {
          stepData.templateId = step.templateId;
        }

        if (step.actionType === 'add_to_list' || step.actionType === 'remove_from_list') {
          stepData.actionConfig = { listId: step.listId };
        }

        if (step.actionType === 'update_field') {
          stepData.actionConfig = {
            fieldName: step.fieldName,
            fieldValue: step.fieldValue
          };
        }

        return stepData;
      });

      const body = editingWorkflow === 'new' 
        ? { ...workflowForm, steps }
        : { ...workflowForm, id: editingWorkflow, steps };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (result.success) {
        alert(editingWorkflow === 'new' ? 'Workflow aangemaakt!' : 'Workflow bijgewerkt!');
        closeWorkflowEditor();
        fetchMailingData('workflows');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert('Error saving workflow: ' + error.message);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Weet je zeker dat je deze workflow wilt verwijderen?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/mailing/workflows?id=${workflowId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        alert('Workflow verwijderd!');
        fetchMailingData('workflows');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      alert('Error deleting workflow: ' + error.message);
    }
  };

  const renderWorkflowsContent = () => {
    const workflows = mailingData.workflows || [];
    const templates = mailingData.templates || [];
    const lists = mailingData.lists || [];

    // If editing, show editor
    if (editingWorkflow) {
      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingWorkflow === 'new' ? 'Nieuwe Workflow' : 'Workflow Bewerken'}</h3>
            <button 
              className="admin-button-secondary"
              onClick={closeWorkflowEditor}
            >
              Annuleren
            </button>
          </div>

          <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Workflow Naam *
              </label>
              <input
                type="text"
                value={workflowForm.name}
                onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="Bijv: Welcome Series"
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Beschrijving
              </label>
              <textarea
                value={workflowForm.description}
                onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                style={{ width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="Beschrijving van de workflow"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Trigger Type *
                </label>
                <select
                  value={workflowForm.triggerType}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, triggerType: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  {triggerTypes.map(trigger => (
                    <option key={trigger.value} value={trigger.value} title={trigger.description}>
                      {trigger.label}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {triggerTypes.find(t => t.value === workflowForm.triggerType)?.description}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Status
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={workflowForm.isActive}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, isActive: e.target.checked })}
                  />
                  <span>Actief</span>
                </label>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4>Acties (Stappen)</h4>
              <button 
                className="admin-button-secondary"
                onClick={addWorkflowStep}
              >
                + Stap Toevoegen
              </button>
            </div>

            {workflowForm.steps.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                Geen stappen toegevoegd. Klik op "+ Stap Toevoegen" om een actie toe te voegen.
              </div>
            ) : (
              workflowForm.steps.map((step, index) => (
                <div key={index} style={{ marginBottom: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h5 style={{ margin: 0 }}>Stap {index + 1}</h5>
                    <button
                      className="admin-button-small"
                      onClick={() => removeWorkflowStep(index)}
                      style={{ backgroundColor: '#f44336', color: 'white' }}
                    >
                      Verwijderen
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Actie Type *
                      </label>
                      <select
                        value={step.actionType}
                        onChange={(e) => updateWorkflowStep(index, 'actionType', e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        {actionTypes.map(action => (
                          <option key={action.value} value={action.value}>
                            {action.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {step.actionType === 'send_email' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                          Template *
                        </label>
                        <select
                          value={step.templateId}
                          onChange={(e) => updateWorkflowStep(index, 'templateId', e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="">Selecteer template</option>
                          {templates.filter(t => t.isActive).map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {(step.actionType === 'add_to_list' || step.actionType === 'remove_from_list') && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                          Maillijst *
                        </label>
                        <select
                          value={step.listId}
                          onChange={(e) => updateWorkflowStep(index, 'listId', e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="">Selecteer lijst</option>
                          {lists.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {step.actionType === 'update_field' && (
                      <>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Veld Naam *
                          </label>
                          <input
                            type="text"
                            value={step.fieldName}
                            onChange={(e) => updateWorkflowStep(index, 'fieldName', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            placeholder="Bijv: store, country"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Veld Waarde *
                          </label>
                          <input
                            type="text"
                            value={step.fieldValue}
                            onChange={(e) => updateWorkflowStep(index, 'fieldValue', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            placeholder="Bijv: VIP"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Vertraging (Dagen)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={step.delayDays}
                        onChange={(e) => updateWorkflowStep(index, 'delayDays', parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Vertraging (Uren)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={step.delayHours}
                        onChange={(e) => updateWorkflowStep(index, 'delayHours', parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Vertraging (Minuten)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={step.delayMinutes}
                        onChange={(e) => updateWorkflowStep(index, 'delayMinutes', parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={step.isActive}
                        onChange={(e) => updateWorkflowStep(index, 'isActive', e.target.checked)}
                      />
                      <span>Stap is actief</span>
                    </label>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="admin-button-primary"
              onClick={saveWorkflow}
              disabled={workflowLoading}
            >
              {workflowLoading ? 'Opslaan...' : editingWorkflow === 'new' ? 'Workflow Aanmaken' : 'Workflow Opslaan'}
            </button>
            <button
              className="admin-button-secondary"
              onClick={closeWorkflowEditor}
              disabled={workflowLoading}
            >
              Annuleren
            </button>
          </div>
        </div>
      );
    }

    // Show workflow list
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Email Workflows</h3>
          <button 
            className="admin-button-primary"
            onClick={() => openWorkflowEditor()}
          >
            + Nieuwe Workflow
          </button>
        </div>

        {workflows.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Trigger Type</th>
                  <th>Stappen</th>
                  <th>Uitvoeringen</th>
                  <th>Actief</th>
                  <th>Datum</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {workflows.map((workflow) => (
                  <tr key={workflow.id}>
                    <td>{workflow.name}</td>
                    <td>{triggerTypes.find(t => t.value === workflow.triggerType)?.label || workflow.triggerType}</td>
                    <td>{workflow._count?.steps || 0}</td>
                    <td>{workflow._count?.executions || 0}</td>
                    <td>{workflow.isActive ? '✓' : '✗'}</td>
                    <td>{new Date(workflow.createdAt).toLocaleDateString('nl-NL')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button 
                          className="admin-button-small"
                          onClick={() => openWorkflowEditor(workflow)}
                        >
                          Bewerken
                        </button>
                        <button 
                          className="admin-button-small"
                          onClick={() => deleteWorkflow(workflow.id)}
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                        >
                          Verwijderen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Geen workflows gevonden. Klik op "Nieuwe Workflow" om er een aan te maken.
          </div>
        )}
      </div>
    );
  };

  const openListEditor = (list = null) => {
    if (list) {
      setListForm({
        name: list.name || '',
        description: list.description || '',
        isPublic: list.isPublic !== undefined ? list.isPublic : false,
        doubleOptIn: list.doubleOptIn !== undefined ? list.doubleOptIn : true
      });
      setEditingList(list.id);
    } else {
      setListForm({
        name: '',
        description: '',
        isPublic: false,
        doubleOptIn: true
      });
      setEditingList('new');
    }
  };

  const closeListEditor = () => {
    setEditingList(null);
    setListForm({
      name: '',
      description: '',
      isPublic: false,
      doubleOptIn: true
    });
  };

  const saveList = async () => {
    if (!listForm.name) {
      alert('Naam is verplicht');
      return;
    }

    setListLoading(true);
    try {
      const url = '/api/admin/mailing/lists';
      const method = editingList === 'new' ? 'POST' : 'PUT';
      const body = editingList === 'new' 
        ? listForm 
        : { ...listForm, id: editingList };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Response is not JSON: ${text.substring(0, 200)}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(editingList === 'new' ? 'Maillijst aangemaakt!' : 'Maillijst bijgewerkt!');
        closeListEditor();
        fetchMailingData('lists');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving list:', error);
      alert('Error saving list: ' + error.message);
    } finally {
      setListLoading(false);
    }
  };

  const fetchListMembers = async (listId, statusFilter = '') => {
    setListMembersLoading(true);
    try {
      let url = `/api/admin/mailing/list-members?listId=${listId}&pageSize=100`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        const text = await response.text();
        console.error(`Error fetching list members (${response.status}):`, text);
        return;
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Error fetching list members: Response is not JSON:', text.substring(0, 200));
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        setListMembers(result.data);
      } else {
        console.error('Error fetching list members:', result.error);
      }
    } catch (error) {
      console.error('Error fetching list members:', error);
    } finally {
      setListMembersLoading(false);
    }
  };

  const addMembersToList = async (listId, consumerIds) => {
    if (!consumerIds || consumerIds.length === 0) {
      alert('Selecteer minimaal één consument');
      return;
    }

    try {
      const response = await authenticatedFetch('/api/admin/mailing/list-members', {
        method: 'POST',
        body: JSON.stringify({
          listId: listId,
          consumerIds: Array.from(consumerIds),
          status: 'subscribed',
          source: 'manual'
        })
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Response is not JSON: ${text.substring(0, 200)}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`✅ ${result.data.added} consument(en) toegevoegd aan lijst!`);
        setSelectedConsumers(new Set());
        setBulkAddToListId(null);
        if (viewingListMembers === listId) {
          fetchListMembers(listId);
        }
        fetchMailingData('lists');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding members to list:', error);
      alert('Error adding members: ' + error.message);
    }
  };

  const addFilteredConsumersToList = async (listId) => {
    // Build filters object from current filters
    const filters = {};
    if (consumerSearchQuery) filters.search = consumerSearchQuery;
    if (consumerStoreFilter) filters.store = consumerStoreFilter;
    if (consumerCountryFilter) filters.country = consumerCountryFilter;
    filters.isUnsubscribed = false; // Only add non-unsubscribed consumers

    if (Object.keys(filters).length === 0) {
      alert('Selecteer eerst filters om bulk toevoegen te gebruiken');
      return;
    }

    // Confirm action
    const confirmed = window.confirm(
      `Weet je zeker dat je alle consumenten die voldoen aan de huidige filters wilt toevoegen aan deze lijst?\n\n` +
      `Actieve filters:\n` +
      `${consumerSearchQuery ? `- Zoek: "${consumerSearchQuery}"\n` : ''}` +
      `${consumerStoreFilter ? `- Store: ${consumerStoreFilter}\n` : ''}` +
      `${consumerCountryFilter ? `- Land: ${consumerCountryFilter}\n` : ''}\n` +
      `Dit kan duizenden consumenten zijn.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await authenticatedFetch('/api/admin/mailing/list-members', {
        method: 'POST',
        body: JSON.stringify({
          listId: listId,
          filters: filters,
          status: 'subscribed',
          source: 'bulk_filter'
        })
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Response is not JSON: ${text.substring(0, 200)}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`✅ ${result.data.added.toLocaleString('nl-NL')} consument(en) toegevoegd aan lijst!\n\n` +
              `Totaal gevonden: ${result.data.total.toLocaleString('nl-NL')}\n` +
              `Overgeslagen: ${result.data.skipped || 0}`);
        if (viewingListMembers === listId) {
          fetchListMembers(listId);
        }
        fetchMailingData('lists');
        // Refresh consumer list to show updated counts
        fetchMailingData('consumers', consumerSearchQuery, consumerStoreFilter, consumerCountryFilter, consumerUnsubscribedFilter);
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding filtered consumers to list:', error);
      alert('Error adding consumers: ' + error.message);
    }
  };

  const removeMemberFromList = async (listId, consumerId) => {
    if (!window.confirm('Weet je zeker dat je deze consument uit de lijst wilt verwijderen?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/mailing/list-members?listId=${listId}&consumerId=${consumerId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Response is not JSON: ${text.substring(0, 200)}`);
      }

      const result = await response.json();

      if (result.success) {
        alert('✅ Consument verwijderd uit lijst!');
        fetchListMembers(listId);
        fetchMailingData('lists');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error removing member from list:', error);
      alert('Error removing member: ' + error.message);
    }
  };

  const deleteList = async (listId) => {
    if (!window.confirm('Weet je zeker dat je deze maillijst wilt verwijderen? Alle leden worden ook verwijderd.')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/mailing/lists?id=${listId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        alert('Maillijst verwijderd!');
        fetchMailingData('lists');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting list:', error);
      alert('Error deleting list: ' + error.message);
    }
  };

  const renderListsContent = () => {
    const lists = mailingData.lists || [];

    // If editing, show editor
    if (editingList) {
      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingList === 'new' ? 'Nieuwe Maillijst' : 'Maillijst Bewerken'}</h3>
            <button 
              className="admin-button-secondary"
              onClick={closeListEditor}
            >
              Annuleren
            </button>
          </div>

          <div style={{ maxWidth: '600px' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Lijst Naam *
              </label>
              <input
                type="text"
                value={listForm.name}
                onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="Bijv: VIP Klanten"
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Beschrijving
              </label>
              <textarea
                value={listForm.description}
                onChange={(e) => setListForm({ ...listForm, description: e.target.value })}
                style={{ width: '100%', minHeight: '100px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="Beschrijving van de maillijst"
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={listForm.isPublic}
                  onChange={(e) => setListForm({ ...listForm, isPublic: e.target.checked })}
                />
                <span>Publieke lijst (consumenten kunnen zich direct aanmelden)</span>
              </label>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={listForm.doubleOptIn}
                  onChange={(e) => setListForm({ ...listForm, doubleOptIn: e.target.checked })}
                />
                <span>Double Opt-In vereist (email bevestiging nodig)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="admin-button-primary"
                onClick={saveList}
                disabled={listLoading}
              >
                {listLoading ? 'Opslaan...' : editingList === 'new' ? 'Maillijst Aanmaken' : 'Maillijst Opslaan'}
              </button>
              <button
                className="admin-button-secondary"
                onClick={closeListEditor}
                disabled={listLoading}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Show list members view
    if (viewingListMembers) {
      const currentList = lists.find(l => l.id === viewingListMembers);
      
      if (!currentList) {
        setViewingListMembers(null);
        return null;
      }

      // Load members if not loaded
      if (!listMembers && !listMembersLoading) {
        fetchListMembers(viewingListMembers);
      }

      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <button 
                className="admin-button-secondary"
                onClick={() => {
                  setViewingListMembers(null);
                  setListMembers(null);
                }}
                style={{ marginRight: '10px' }}
              >
                ← Terug
              </button>
              <h3 style={{ display: 'inline', marginLeft: '10px' }}>
                Leden van: {currentList.name}
              </h3>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <select
                value={listMemberStatusFilter}
                onChange={(e) => {
                  setListMemberStatusFilter(e.target.value);
                  fetchListMembers(viewingListMembers, e.target.value);
                }}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Alle Status</option>
                <option value="subscribed">Ingeschreven</option>
                <option value="unsubscribed">Uitgeschreven</option>
                <option value="pending">In Afwachting</option>
              </select>
              
              <button 
                className="admin-button-primary"
                onClick={() => {
                  setMailingSubsection('consumers');
                  setBulkAddToListId(viewingListMembers);
                  if (!mailingData.lists) {
                    fetchMailingData('lists');
                  }
                }}
              >
                + Consumenten Toevoegen
              </button>
            </div>
          </div>

          {listMembersLoading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>Laden...</div>
          ) : listMembers && listMembers.members && listMembers.members.length > 0 ? (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Naam</th>
                    <th>Email</th>
                    <th>Store</th>
                    <th>Status</th>
                    <th>Toegevoegd</th>
                    <th>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {listMembers.members.map((member) => (
                    <tr key={member.id}>
                      <td>{member.consumer.firstName} {member.consumer.lastName}</td>
                      <td>{member.consumer.email}</td>
                      <td>{member.consumer.store}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: member.status === 'subscribed' ? '#4caf50' : '#ff9800',
                          color: 'white'
                        }}>
                          {member.status}
                        </span>
                      </td>
                      <td>{new Date(member.subscribedAt).toLocaleDateString('nl-NL')}</td>
                      <td>
                        <button 
                          className="admin-button-small"
                          onClick={() => removeMemberFromList(viewingListMembers, member.consumerId)}
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                        >
                          Verwijderen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {listMembers.pagination && listMembers.pagination.totalPages > 1 && (
                <div style={{ marginTop: '15px', textAlign: 'center', color: '#666' }}>
                  Pagina {listMembers.pagination.page} van {listMembers.pagination.totalPages} 
                  ({listMembers.pagination.total} totaal)
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              Geen leden in deze lijst. Klik op "Consumenten Toevoegen" om leden toe te voegen.
            </div>
          )}
        </div>
      );
    }

    // Show lists
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Maillijsten</h3>
          <button 
            className="admin-button-primary"
            onClick={() => openListEditor()}
          >
            + Nieuwe Maillijst
          </button>
        </div>

        {lists.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Beschrijving</th>
                  <th>Leden</th>
                  <th>Unsubscribed</th>
                  <th>Publiek</th>
                  <th>Double Opt-In</th>
                  <th>Datum</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {lists.map((list) => (
                  <tr key={list.id}>
                    <td>{list.name}</td>
                    <td>{list.description || '-'}</td>
                    <td>{list.totalConsumers || list._count?.listMembers || 0}</td>
                    <td>{list.unsubscribedCount || 0}</td>
                    <td>{list.isPublic ? '✓' : '✗'}</td>
                    <td>{list.doubleOptIn ? '✓' : '✗'}</td>
                    <td>{new Date(list.createdAt).toLocaleDateString('nl-NL')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button 
                          className="admin-button-small"
                          onClick={() => {
                            setViewingListMembers(list.id);
                            fetchListMembers(list.id);
                          }}
                          style={{ backgroundColor: '#2196f3', color: 'white' }}
                        >
                          👥 Leden
                        </button>
                        <button 
                          className="admin-button-small"
                          onClick={() => openListEditor(list)}
                        >
                          Bewerken
                        </button>
                        <button 
                          className="admin-button-small"
                          onClick={() => deleteList(list.id)}
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                        >
                          Verwijderen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Geen maillijsten gevonden. Klik op "Nieuwe Maillijst" om er een aan te maken.
          </div>
        )}
      </div>
    );
  };

  const openCampaignEditor = (campaign = null) => {
    if (campaign) {
      const listId = campaign.filterCriteria?.listId || '';
      setCampaignForm({
        name: campaign.name || '',
        subject: campaign.subject || '',
        templateId: campaign.templateId || '',
        listId: listId,
        scheduledAt: campaign.scheduledAt ? new Date(campaign.scheduledAt).toISOString().slice(0, 16) : '',
        status: campaign.status || 'draft'
      });
      setEditingCampaign(campaign.id);
    } else {
      setCampaignForm({
        name: '',
        subject: '',
        templateId: '',
        listId: '',
        scheduledAt: '',
        status: 'draft'
      });
      setEditingCampaign('new');
    }
    updateCampaignPreview();
  };

  const closeCampaignEditor = () => {
    setEditingCampaign(null);
    setCampaignForm({
      name: '',
      subject: '',
      templateId: '',
      listId: '',
      scheduledAt: '',
      status: 'draft'
    });
    setCampaignPreview('');
    setRecipientCount(0);
  };

  const updateCampaignPreview = () => {
    const selectedTemplate = mailingData.templates?.find(t => t.id === campaignForm.templateId);
    if (selectedTemplate) {
      let preview = selectedTemplate.htmlContent || '';
      // Replace variables with example values
      availableVariables.forEach(variable => {
        const regex = new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g');
        preview = preview.replace(regex, variable.example);
      });
      setCampaignPreview(preview);
    } else {
      setCampaignPreview('');
    }

    // Calculate recipient count
    if (campaignForm.listId) {
      const selectedList = mailingData.lists?.find(l => l.id === campaignForm.listId);
      if (selectedList) {
        setRecipientCount(selectedList.totalConsumers || selectedList._count?.listMembers || 0);
      } else {
        setRecipientCount(0);
      }
    } else {
      // Count all active consumers
      const consumers = mailingData.consumers?.consumers || [];
      setRecipientCount(consumers.length);
    }
  };

  const saveCampaign = async () => {
    if (!campaignForm.name || !campaignForm.subject) {
      alert('Naam en onderwerp zijn verplicht');
      return;
    }

    if (!campaignForm.templateId) {
      alert('Selecteer een template');
      return;
    }

    if (!campaignForm.listId) {
      alert('Selecteer een maillijst');
      return;
    }

    setCampaignLoading(true);
    try {
      const url = '/api/admin/mailing/campaigns';
      const method = editingCampaign === 'new' ? 'POST' : 'PUT';
      const body = editingCampaign === 'new' 
        ? {
            ...campaignForm,
            scheduledAt: campaignForm.scheduledAt || null
          }
        : {
            ...campaignForm,
            id: editingCampaign,
            scheduledAt: campaignForm.scheduledAt || null
          };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (result.success) {
        alert(editingCampaign === 'new' ? 'Campagne aangemaakt!' : 'Campagne bijgewerkt!');
        closeCampaignEditor();
        fetchMailingData('campaigns');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Error saving campaign: ' + error.message);
    } finally {
      setCampaignLoading(false);
    }
  };

  const sendCampaign = async (campaignId) => {
    if (!window.confirm('Weet je zeker dat je deze campagne wilt versturen?')) {
      return;
    }

    setCampaignLoading(true);
    try {
      const response = await authenticatedFetch('/api/admin/mailing/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          action: 'send',
          id: campaignId
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'Campagne wordt verstuurd!');
        fetchMailingData('campaigns');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('Error sending campaign: ' + error.message);
    } finally {
      setCampaignLoading(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Weet je zeker dat je deze campagne wilt verwijderen?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/mailing/campaigns?id=${campaignId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        alert('Campagne verwijderd!');
        fetchMailingData('campaigns');
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Error deleting campaign: ' + error.message);
    }
  };

  const renderCampaignsContent = () => {
    const campaigns = mailingData.campaigns?.campaigns || [];
    const pagination = mailingData.campaigns?.pagination;
    const templates = mailingData.templates || [];
    const lists = mailingData.lists || [];

    // If editing, show editor
    if (editingCampaign) {
      const selectedTemplate = templates.find(t => t.id === campaignForm.templateId);
      const selectedList = lists.find(l => l.id === campaignForm.listId);

      return (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingCampaign === 'new' ? 'Nieuwe Campagne' : 'Campagne Bewerken'}</h3>
            <button 
              className="admin-button-secondary"
              onClick={closeCampaignEditor}
            >
              Annuleren
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Left Column: Form */}
            <div>
              <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Campagne Naam *
                  </label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => {
                      setCampaignForm({ ...campaignForm, name: e.target.value });
                      updateCampaignPreview();
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    placeholder="Bijv: Weekly Newsletter"
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Onderwerp *
                  </label>
                  <input
                    type="text"
                    value={campaignForm.subject}
                    onChange={(e) => {
                      setCampaignForm({ ...campaignForm, subject: e.target.value });
                      updateCampaignPreview();
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    placeholder="Email onderwerp"
                  />
                  {selectedTemplate && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      Template onderwerp: {selectedTemplate.subject}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Template *
                  </label>
                  <select
                    value={campaignForm.templateId}
                    onChange={(e) => {
                      setCampaignForm({ ...campaignForm, templateId: e.target.value });
                      const template = templates.find(t => t.id === e.target.value);
                      if (template && !campaignForm.subject) {
                        setCampaignForm(prev => ({ ...prev, subject: template.subject }));
                      }
                      updateCampaignPreview();
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Selecteer template</option>
                    {templates.filter(t => t.isActive).map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Maillijst *
                  </label>
                  <select
                    value={campaignForm.listId}
                    onChange={(e) => {
                      setCampaignForm({ ...campaignForm, listId: e.target.value });
                      updateCampaignPreview();
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Selecteer maillijst</option>
                    {lists.map(list => (
                      <option key={list.id} value={list.id}>
                        {list.name} ({list.totalConsumers || list._count?.listMembers || 0} leden)
                      </option>
                    ))}
                  </select>
                  {recipientCount > 0 && (
                    <div style={{ fontSize: '12px', color: '#4caf50', marginTop: '5px', fontWeight: 'bold' }}>
                      📧 {recipientCount} ontvangers
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Geplande Verzendtijd (Optioneel)
                  </label>
                  <input
                    type="datetime-local"
                    value={campaignForm.scheduledAt}
                    onChange={(e) => setCampaignForm({ ...campaignForm, scheduledAt: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Laat leeg om direct te versturen na opslaan
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="admin-button-primary"
                  onClick={saveCampaign}
                  disabled={campaignLoading}
                >
                  {campaignLoading ? 'Opslaan...' : editingCampaign === 'new' ? 'Campagne Opslaan' : 'Campagne Bijwerken'}
                </button>
                {editingCampaign !== 'new' && (
                  <button
                    className="admin-button-secondary"
                    onClick={() => sendCampaign(editingCampaign)}
                    disabled={campaignLoading || campaignForm.status === 'sending' || campaignForm.status === 'sent'}
                    style={{ backgroundColor: '#4caf50', color: 'white' }}
                  >
                    📧 Versturen
                  </button>
                )}
                <button
                  className="admin-button-secondary"
                  onClick={closeCampaignEditor}
                  disabled={campaignLoading}
                >
                  Annuleren
                </button>
              </div>
            </div>

            {/* Right Column: Preview */}
            <div>
              <h4 style={{ marginBottom: '15px' }}>Preview</h4>
              {selectedTemplate ? (
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '8px', 
                  padding: '20px',
                  backgroundColor: 'white',
                  minHeight: '400px'
                }}>
                  <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
                    <strong>Onderwerp:</strong> {campaignForm.subject || selectedTemplate.subject || '(geen onderwerp)'}
                  </div>
                  <div 
                    dangerouslySetInnerHTML={{ __html: campaignPreview || '<p style="color: #999;">Selecteer een template om preview te zien</p>' }}
                    style={{ 
                      lineHeight: '1.6',
                      color: '#333'
                    }}
                  />
                </div>
              ) : (
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '8px', 
                  padding: '20px',
                  backgroundColor: '#f5f5f5',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999'
                }}>
                  Selecteer een template om preview te zien
                </div>
              )}
              {selectedList && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '6px', fontSize: '12px' }}>
                  📋 <strong>Geselecteerde lijst:</strong> {selectedList.name}<br/>
                  👥 <strong>Leden:</strong> {selectedList.totalConsumers || selectedList._count?.listMembers || 0}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Show campaign list
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Email Campagnes</h3>
          <button 
            className="admin-button-primary"
            onClick={() => {
              // Ensure templates and lists are loaded
              if (!mailingData.templates) fetchMailingData('templates');
              if (!mailingData.lists) fetchMailingData('lists');
              openCampaignEditor();
            }}
          >
            + Nieuwe Campagne
          </button>
        </div>

        {pagination && (
          <div style={{ marginBottom: '15px', color: '#666' }}>
            Totaal: {pagination.total} campagnes
          </div>
        )}

        {campaigns.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Onderwerp</th>
                  <th>Status</th>
                  <th>Template</th>
                  <th>Ontvangers</th>
                  <th>Verstuurd</th>
                  <th>Geopend</th>
                  <th>Geklikt</th>
                  <th>Gebounced</th>
                  <th>Datum</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const listId = campaign.filterCriteria?.listId;
                  const list = lists.find(l => l.id === listId);
                  
                  return (
                    <tr key={campaign.id}>
                      <td>{campaign.name}</td>
                      <td>{campaign.subject}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: 
                            campaign.status === 'sent' ? '#4caf50' :
                            campaign.status === 'sending' ? '#ff9800' :
                            campaign.status === 'scheduled' ? '#2196f3' :
                            '#e0e0e0',
                          color: campaign.status === 'draft' ? '#666' : 'white'
                        }}>
                          {campaign.status}
                        </span>
                      </td>
                      <td>{campaign.template?.name || '-'}</td>
                      <td>
                        {campaign.totalRecipients || 0}
                        {list && <span style={{ fontSize: '11px', color: '#666', marginLeft: '5px' }}>({list.name})</span>}
                      </td>
                      <td>{campaign.totalSent}</td>
                      <td>{campaign.totalOpened}</td>
                      <td>{campaign.totalClicked}</td>
                      <td>{campaign.totalBounced}</td>
                      <td>{new Date(campaign.createdAt).toLocaleDateString('nl-NL')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            className="admin-button-small"
                            onClick={() => {
                              if (!mailingData.templates) fetchMailingData('templates');
                              if (!mailingData.lists) fetchMailingData('lists');
                              openCampaignEditor(campaign);
                            }}
                          >
                            Bewerken
                          </button>
                          {campaign.status === 'draft' && (
                            <button 
                              className="admin-button-small"
                              onClick={() => sendCampaign(campaign.id)}
                              style={{ backgroundColor: '#4caf50', color: 'white' }}
                            >
                              Versturen
                            </button>
                          )}
                          <button 
                            className="admin-button-small"
                            onClick={() => deleteCampaign(campaign.id)}
                            style={{ backgroundColor: '#f44336', color: 'white' }}
                          >
                            Verwijderen
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Geen campagnes gevonden. Klik op "Nieuwe Campagne" om er een aan te maken.
          </div>
        )}
      </div>
    );
  };

  const renderBouncesContent = () => {
    const bounces = mailingData.bounces?.bounces || [];
    const statistics = mailingData.bounces?.statistics;
    const pagination = mailingData.bounces?.pagination;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Email Bounces</h3>
          <button className="admin-button-secondary">Cleanup Oude Bounces</button>
        </div>

        {statistics && (
          <div className="admin-stats-grid" style={{ marginBottom: '20px' }}>
            <div className="stat-card">
              <h3>Totaal Bounces</h3>
              <p className="stat-number">{statistics.total}</p>
            </div>
            <div className="stat-card">
              <h3>Hard Bounces</h3>
              <p className="stat-number">{statistics.hard}</p>
            </div>
            <div className="stat-card">
              <h3>Soft Bounces</h3>
              <p className="stat-number">{statistics.soft}</p>
            </div>
          </div>
        )}

        {pagination && (
          <div style={{ marginBottom: '15px', color: '#666' }}>
            Totaal: {pagination.total} bounces
          </div>
        )}

        {bounces.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Naam</th>
                  <th>Campagne</th>
                  <th>Type</th>
                  <th>Reden</th>
                  <th>Datum</th>
                </tr>
              </thead>
              <tbody>
                {bounces.map((bounce) => (
                  <tr key={bounce.id}>
                    <td>{bounce.consumer?.email || '-'}</td>
                    <td>{bounce.consumer ? `${bounce.consumer.firstName} ${bounce.consumer.lastName}` : '-'}</td>
                    <td>{bounce.campaign?.name || '-'}</td>
                    <td>
                      <span style={{ 
                        color: bounce.bounceType === 'hard' ? 'red' : 'orange',
                        fontWeight: 'bold'
                      }}>
                        {bounce.bounceType || '-'}
                      </span>
                    </td>
                    <td>{bounce.bounceReason || '-'}</td>
                    <td>{new Date(bounce.occurredAt).toLocaleDateString('nl-NL')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Geen bounces gevonden
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-container">
      <header className="admin-header-top">
        <Link to="/" className="admin-logo-link">
          <img src="/images/logo_sdeal_navbar.svg" alt="SDeal Logo" className="admin-logo" />
        </Link>
        <div className="admin-header-user">
          <span className="admin-header-name">{user?.firstName || 'Admin'}</span>
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>
      <div className="admin-body">
        <AdminSidebar activeSection={activeSection} onSelectSection={setActiveSection} />
        <main className="admin-main">
          {renderSectionContent()}
        </main>
      </div>

      {/* Seller Info Modal */}
      {(selectedSeller || sellerInfo) && (
        <div className="seller-modal-overlay" onClick={closeSellerModal}>
          <div className="seller-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="seller-modal-header">
              <h2>Seller Informatie - ID: {selectedSeller}</h2>
              <button className="seller-modal-close" onClick={closeSellerModal}>×</button>
            </div>
            
            <div className="seller-modal-body">
              {loadingSellerInfo ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="admin-loading">Laden...</div>
                </div>
              ) : sellerInfoError ? (
                <div style={{ color: 'red', padding: '20px', textAlign: 'center' }}>
                  {sellerInfoError}
                </div>
              ) : sellerInfo ? (
                <div className="seller-info-sections">
                  {/* Supplier View (API: supplier + addresses + contacts + packages) */}
                  {(sellerInfo.sellerView || sellerInfo.errors.sellerView) && (
                    <div className="seller-info-section">
                      <h3>Supplier (view)</h3>
                      {sellerInfo.errors.sellerView ? (
                        <div style={{ color: 'red' }}>Error: {sellerInfo.errors.sellerView}</div>
                      ) : sellerInfo.sellerView && Array.isArray(sellerInfo.sellerView) && sellerInfo.sellerView[0] ? (
                        <>
                          {(() => {
                            const view = sellerInfo.sellerView[0];
                            const sup = view.supplier || {};
                            return (
                              <>
                                <div className="seller-info-card" style={{ marginBottom: '1rem' }}>
                                  <div className="seller-info-row"><strong>Naam:</strong> {sup.name || '-'}</div>
                                  <div className="seller-info-row"><strong>ID:</strong> {sup.id ?? '-'}</div>
                                  <div className="seller-info-row"><strong>Status:</strong> {sup.is_active === 1 ? 'Enabled' : sup.is_active === 0 ? 'Disabled' : (sup.is_active ?? '-')}</div>
                                  <div className="seller-info-row"><strong>Commission:</strong> {sup.commission ?? '-'}%</div>
                                  <div className="seller-info-row"><strong>URL key:</strong> {sup.url_key || '-'}</div>
                                  <div className="seller-info-row"><strong>Official seller:</strong> {sup.official_seller ? 'Ja' : 'Nee'}</div>
                                  <div className="seller-info-row"><strong>Created:</strong> {sup.created_at ? new Date(sup.created_at).toLocaleString('nl-NL') : '-'}</div>
                                  <div className="seller-info-row"><strong>Updated:</strong> {sup.updated_at ? new Date(sup.updated_at).toLocaleString('nl-NL') : '-'}</div>
                                  {sup.description && (
                                    <div className="seller-info-row" style={{ marginTop: '0.5rem' }}>
                                      <strong>Description:</strong>
                                      <div style={{ marginTop: '0.25rem', whiteSpace: 'pre-wrap', maxHeight: '120px', overflowY: 'auto' }}>{sup.description.replace(/<[^>]+>/g, ' ')}</div>
                                    </div>
                                  )}
                                  {sup.notes && (
                                    <div className="seller-info-row" style={{ marginTop: '0.5rem' }}><strong>Notes:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{sup.notes}</span></div>
                                  )}
                                  {sup.service && (
                                    <div className="seller-info-row" style={{ marginTop: '0.5rem' }}><strong>Service:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{sup.service}</span></div>
                                  )}
                                </div>
                                {view.addresses && view.addresses.length > 0 && (
                                  <div style={{ marginTop: '1rem' }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Adressen</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                      <thead><tr style={{ borderBottom: '1px solid #ddd' }}><th style={{ textAlign: 'left', padding: '6px 8px' }}>Type</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Straat</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Postcode</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Plaats</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Land</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Active</th></tr></thead>
                                      <tbody>
                                        {view.addresses.map((addr) => (
                                          <tr key={addr.id} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '6px 8px' }}>{addr.type || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{[addr.street, addr.housenumber].filter(Boolean).join(' ') || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{addr.postcode || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{addr.city || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{addr.country_code || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{addr.is_active === 1 ? 'Ja' : 'Nee'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {view.contacts && view.contacts.length > 0 && (
                                  <div style={{ marginTop: '1rem' }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Contacten</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                      <thead><tr style={{ borderBottom: '1px solid #ddd' }}><th style={{ textAlign: 'left', padding: '6px 8px' }}>Type</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Naam</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Email</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Telefoon</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Active</th></tr></thead>
                                      <tbody>
                                        {view.contacts.map((c) => (
                                          <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '6px 8px' }}>{c.type || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{[c.firstname, c.lastname].filter(Boolean).join(' ') || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{c.emailaddress || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{c.phonenumber || '-'}</td>
                                            <td style={{ padding: '6px 8px' }}>{c.is_active === 1 ? 'Ja' : 'Nee'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {view.packages && view.packages.length > 0 && (() => {
                                  const pkgKeys = [...new Set(view.packages.flatMap((p) => Object.keys(p)))].filter((k) => !k.match(/^_/)).sort();
                                  const displayKeys = ['id', 'prijs_display', 'packages_type', 'name', 'description', 'type_id', 'status', 'is_active', 'created_at', 'updated_at', ...pkgKeys.filter((k) => !['id', 'packages_type', 'name', 'description', 'type_id', 'status', 'is_active', 'created_at', 'updated_at'].includes(k))];
                                  const getPriceForPackage = (p) => {
                                    const id = getPackageId(p);
                                    const info = id != null ? PACKAGE_PRICES[id] : null;
                                    return info != null ? `€${Number(info.price) === info.price ? info.price : info.price.toFixed(2)}` : '-';
                                  };
                                  const monthlyRevenue = getMonthlyRevenueForPackages(view.packages);
                                  return (
                                    <div style={{ marginTop: '1rem' }}>
                                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Packages</h4>
                                      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>
                                        Verwachte maandelijkse revenue: €{monthlyRevenue.toFixed(2)}
                                      </p>
                                      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#555' }}>
                                        Opbrengsten per package: {view.packages.map((p) => {
                                          const id = getPackageId(p);
                                          const info = id != null ? PACKAGE_PRICES[id] : null;
                                          return info ? `ID ${id} = €${Number(info.price) === info.price ? info.price : info.price.toFixed(2)}` : null;
                                        }).filter(Boolean).join(', ') || '-'}
                                      </p>
                                      <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                          <thead><tr style={{ borderBottom: '1px solid #ddd' }}>{displayKeys.map((k) => <th key={k} style={{ textAlign: 'left', padding: '6px 8px', whiteSpace: 'nowrap' }}>{k === 'prijs_display' ? 'Prijs' : k.replace(/_/g, ' ')}</th>)}</tr></thead>
                                          <tbody>
                                            {view.packages.map((p, pIdx) => (
                                              <tr key={p.id ?? p.package_id ?? pIdx} style={{ borderBottom: '1px solid #eee' }}>
                                                {displayKeys.map((key) => {
                                                  if (key === 'prijs_display') {
                                                    return <td key={key} style={{ padding: '6px 8px', fontWeight: 600 }}>{getPriceForPackage(p)}</td>;
                                                  }
                                                  const val = p[key];
                                                  let display = val == null ? '-' : String(val);
                                                  if (key === 'created_at' || key === 'updated_at') display = val ? new Date(val).toLocaleString('nl-NL') : '-';
                                                  if (key === 'is_active') display = val === 1 || val === true ? 'Ja' : val === 0 || val === false ? 'Nee' : display;
                                                  if (key === 'description' && display.length > 80) display = display.slice(0, 80) + '…';
                                                  return <td key={key} style={{ padding: '6px 8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={val != null && String(val).length > 80 ? String(val) : undefined}>{display}</td>;
                                                })}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })()}
                                </>
                              );
                          })()}
                        </>
                      ) : (
                        <div>Geen supplier view data</div>
                      )}
                    </div>
                  )}

                  {/* Balance Info */}
                  <div className="seller-info-section">
                    <h3>Balance</h3>
                    {sellerInfo.errors.balance ? (
                      <div style={{ color: 'red' }}>Error: {sellerInfo.errors.balance}</div>
                    ) : sellerInfo.balance && sellerInfo.balance.items && sellerInfo.balance.items.length > 0 ? (
                      <div className="seller-info-grid">
                        {sellerInfo.balance.items.map((item, idx) => (
                          <div key={idx} className="seller-info-card">
                            <div className="seller-info-row">
                              <strong>Supplier:</strong> {item.supplier_name || '-'}
                            </div>
                            <div className="seller-info-row">
                              <strong>Balance Totaal:</strong> €{parseFloat(item.balance_total || 0).toFixed(2)}
                            </div>
                            <div className="seller-info-row">
                              <strong>Balance Pending:</strong> €{parseFloat(item.balance_pending || 0).toFixed(2)}
                            </div>
                            <div className="seller-info-row">
                              <strong>Balance Available:</strong> €{parseFloat(item.balance_available || 0).toFixed(2)}
                            </div>
                            <div className="seller-info-row">
                              <strong>Balance Partner:</strong> €{parseFloat(item.balance_partner || 0).toFixed(2)}
                            </div>
                            <div className="seller-info-row">
                              <strong>Datum:</strong> {item.date ? new Date(item.date).toLocaleDateString('nl-NL') : '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>Geen balance data beschikbaar</div>
                    )}
                  </div>

                  {/* Orders Info */}
                  <div className="seller-info-section">
                    <h3>Orders (Laatste 10)</h3>
                    {sellerInfo.errors.orders ? (
                      <div style={{ color: 'red' }}>Error: {sellerInfo.errors.orders}</div>
                    ) : sellerInfo.orders && sellerInfo.orders.items && sellerInfo.orders.items.length > 0 ? (
                      <>
                        {(() => {
                          const items = sellerInfo.orders.items;
                          const totalOmzet = items.reduce((sum, o) => sum + parseFloat(o.order_amount_org || 0), 0);
                          return (
                            <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 600 }}>
                              Totale omzet (laatste {items.length} orders): €{totalOmzet.toFixed(2)}
                            </p>
                          );
                        })()}
                        <div className="seller-orders-table">
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th>Order ID</th>
                                <th>Status</th>
                                <th>Finance Status</th>
                                <th>Bedrag</th>
                                <th>Datum</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sellerInfo.orders.items.map((order) => (
                                <tr key={order.id}>
                                  <td>{order.id}</td>
                                  <td>{order.order_status}</td>
                                  <td>{order.finance_status}</td>
                                  <td>€{parseFloat(order.order_amount_org || 0).toFixed(2)}</td>
                                  <td>{order.created_at ? new Date(order.created_at).toLocaleDateString('nl-NL') : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div>Geen orders gevonden</div>
                    )}
                  </div>

                  {/* Delivery Info */}
                  <div className="seller-info-section">
                    <h3>Delivery Informatie</h3>
                    {sellerInfo.errors.deliveryInfo ? (
                      <div style={{ color: 'red' }}>Error: {sellerInfo.errors.deliveryInfo}</div>
                    ) : sellerInfo.deliveryInfo && sellerInfo.deliveryInfo.length > 0 ? (
                      <div className="seller-info-grid">
                        {sellerInfo.deliveryInfo.map((info, idx) => (
                          <div key={idx} className="seller-info-card">
                            <div className="seller-info-row">
                              <strong>Delivery Reliability:</strong> {info.delivery_reliability || 0}%
                            </div>
                            <div className="seller-info-row">
                              <strong>Delivery Reliability (90 dagen):</strong> {info.delivery_reliability_90_days || 0}%
                            </div>
                            <div className="seller-info-row">
                              <strong>Total Emails Sent:</strong> {info.total_email_sent || 0}
                            </div>
                            <div className="seller-info-row">
                              <strong>Answered:</strong> {info.answered || 0}
                            </div>
                            <div className="seller-info-row">
                              <strong>Not Answered:</strong> {info.not_answered || 0}
                            </div>
                            <div className="seller-info-row">
                              <strong>Received:</strong> {info.received || 0}
                            </div>
                            <div className="seller-info-row">
                              <strong>Not Received:</strong> {info.not_received || 0}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>Geen delivery data beschikbaar</div>
                    )}
                  </div>

                  {/* Reviews */}
                  <div className="seller-info-section">
                    <h3>Reviews ({sellerInfo.reviews ? sellerInfo.reviews.length : 0})</h3>
                    {sellerInfo.errors.reviews ? (
                      <div style={{ color: 'red' }}>Error: {sellerInfo.errors.reviews}</div>
                    ) : sellerInfo.reviews && sellerInfo.reviews.length > 0 ? (
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Datum</th>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Sterren</th>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Titel</th>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Beschrijving</th>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Klant</th>
                              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sellerInfo.reviews.map((review) => (
                              <tr key={review.id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '10px' }}>
                                  {review.created_at ? new Date(review.created_at).toLocaleDateString('nl-NL') : '-'}
                                </td>
                                <td style={{ padding: '10px' }}>
                                  <span style={{ color: review.review_stars >= 4 ? '#4CAF50' : review.review_stars >= 3 ? '#FF9800' : '#F44336' }}>
                                    {'★'.repeat(review.review_stars || 0)}{'☆'.repeat(5 - (review.review_stars || 0))} ({review.review_stars || 0}/5)
                                  </span>
                                </td>
                                <td style={{ padding: '10px' }}>
                                  {review.review_title || '-'}
                                </td>
                                <td style={{ padding: '10px', maxWidth: '300px', wordWrap: 'break-word' }}>
                                  {review.review_description || '-'}
                                </td>
                                <td style={{ padding: '10px' }}>
                                  {review.customer_name || `Klant #${review.customer_id || '-'}`}
                                </td>
                                <td style={{ padding: '10px' }}>
                                  <span style={{ 
                                    color: review.review_is_approved === 1 ? '#4CAF50' : '#FF9800',
                                    fontWeight: 'bold'
                                  }}>
                                    {review.review_is_approved === 1 ? '✓ Goedgekeurd' : '⏳ In afwachting'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div>Geen reviews gevonden</div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;

